<template>
    <div class="box">
        <el-dialog
            title="Assign Account"
            :visible.sync="assign_account.show_dialog"
            width="60vh">
            <el-select v-model="assign_account.assigned"
                       class="w-full"
                       placeholder="Search account to assign"
                       filterable
                       clearable
                       multiple
                       remote
                       :loading="assign_account.loading"
                       :remote-method="searchAccounts">
                <el-option
                    v-for="account in assign_account.accounts"
                    class="p-0"
                    :key="account.id"
                    :label="account.name"
                    :value="account.id">
                    <div class="media">
                        <div class="media-body">
                            <label>{{ account.name }}</label>
                        </div>
                    </div>
                </el-option>
            </el-select>
            <div class="row mt-3">
                <div class="col-md-12 text-right">
                    <el-button type="primary"
                               :loading="assign_account.loading"
                               @click="updateAssignedAccounts">
                        Save
                    </el-button>
                </div>
            </div>
        </el-dialog>
        <el-dialog title="Manage Roles"
                   width="60vh"
                   :visible.sync="assign_roles.show_dialog">
            <el-select v-model="assign_roles.assigned"
                       class="w-full"
                       placeholder="Search role to assign"
                       filterable
                       clearable
                       :loading="assign_roles.loading">
                <el-option class="p-0"
                           :key="role.id"
                           :label="role.name"
                           :value="role.id"
                           v-for="role in customRoles">
                    <div class="media">
                        <div class="media-body">
                            <label>{{ role.name }}</label>
                        </div>
                    </div>
                </el-option>
            </el-select>
            <div class="row mt-3">
                <div class="col-md-12 text-right">
                    <el-button type="primary"
                               @click="updateAssignedRoles">
                        Save
                    </el-button>
                </div>
            </div>
        </el-dialog>
        <div class="box-header pb-0">
            <div class="d-flex">
                <div class="pl-4 pr-3">
                    <router-link :to="{ name: 'Dashboard' }">
                        <el-button size="medium"
                                   class="pl-3 pr-2">
                            <i class="fa fa-chevron-left mr-2"></i>
                        </el-button>
                    </router-link>
                </div>
                <h1 v-if="company && company.id">{{ company.name }}</h1>
            </div>
        </div>

        <div class="padding pt-0 mt-3"
             id="container-account-details"
             v-loading="loading">
            <template v-if="company && company.id">
                <div class="d-flex pb-3"
                     v-if="company.credits < company.max_negative_balance">
                    <el-alert type="error"
                              show-icon
                              :closable=false>
                        <span class="d-block mb-1 font-weight-bold">Account hit the maximum negative balance (Current credits: ${{ company.credits | fixRounding(2) }}). They need to adjust their auto-recharge settings to avoid any service disruptions.</span>
                    </el-alert>
                </div>

                <el-tabs v-model="active_tab"
                         @tab-click="handleTabClick">
                    <el-tab-pane name="company-card"
                                 label="Account">
                        <div v-on:mouseleave="tableLeave">
                            <el-table v-loading="loading_company"
                                      :empty-text="loading ? 'Loading...' : 'No Data Found'"
                                      :data="company_summary"
                                      :default-sort="{prop: 'name', order: 'ascending'}"
                                      @cell-mouse-enter="cellEnter"
                                      fit
                                      stripe
                                      default-expand-all>
                                <el-table-column type="expand">
                                    <template v-slot="scope">
                                        <div class="row align-items-top">
                                            <div class="col-md-6">
                                                <div class="row">
                                                    <div class="col-md-4">
                                                        <p class="mb-1"
                                                           v-if="scope.row.rate">
                                                            Rate: {{ scope.row.rate.name }}
                                                        </p>
                                                        <p class="mb-1"
                                                           v-if="scope.row.rate">
                                                            Use Case: {{ company.rate.use_case ?? '-' }}
                                                        </p>
                                                        <p class="b-a p-2 mb-0"
                                                           v-if="scope.row.rate">
                                                            <span>Local PN: ${{ scope.row.rate.local_pn | fixRounding(1) }}</span>
                                                            <br>
                                                            <span>Local Min: ${{ scope.row.rate.local_min | fixRounding(3) }}</span>
                                                            <br>
                                                            <span>Local SMS: ${{ scope.row.rate.local_sms | fixRounding(3) }}</span>
                                                            <br>
                                                            <span>Local MMS: ${{ scope.row.rate.local_mms | fixRounding(3) }}</span>
                                                        </p>
                                                        <router-link class="text-decoration-underline el-link el-link--primary mt-1"
                                                                     :to="{ name: 'Rate', params: { company_id: company.id, id: scope.row.rate.id } }">
                                                            View rate definition
                                                        </router-link>
                                                    </div>
                                                    <div class="col-md-8"
                                                         v-if="scope.row.yesterday_usage && scope.row.yesterday_usage.twilio_usage">
                                                        <div class="mb-2">
                                                            <span>Local Usage:</span>
                                                            <div class="row no-gutter b-a">
                                                                <div class="col-3 px-2 b-r font-weight-bold">
                                                                    PN
                                                                </div>
                                                                <div class="col-3 px-2 b-r font-weight-bold">
                                                                    Min
                                                                </div>
                                                                <div class="col-3 px-2 b-r font-weight-bold">
                                                                    MMS
                                                                </div>
                                                                <div class="col-3 px-2 font-weight-bold">
                                                                    SMS
                                                                </div>
                                                                <div class="p-0 b-b w-full"></div>
                                                                <div class="col-3 px-2 b-r">
                                                                    ${{ scope.row.yesterday_usage.twilio_usage.local_pn | fixRounding(1) }}
                                                                </div>
                                                                <div class="col-3 px-2 b-r">
                                                                    ${{ scope.row.yesterday_usage.twilio_usage.local_min | fixRounding(2) }}
                                                                </div>
                                                                <div class="col-3 px-2 b-r">
                                                                    ${{ scope.row.yesterday_usage.twilio_usage.local_mms | fixRounding(2) }}
                                                                </div>
                                                                <div class="col-3 px-2">
                                                                    ${{ scope.row.yesterday_usage.twilio_usage.local_sms | fixRounding(2) }}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <span>Toll-Free Usage:</span>
                                                            <div class="row no-gutter b-a">
                                                                <div class="col-4 px-2 b-r font-weight-bold">
                                                                    PN
                                                                </div>
                                                                <div class="col-4 px-2 b-r font-weight-bold">
                                                                    Min
                                                                </div>
                                                                <div class="col-4 px-2 font-weight-bold">
                                                                    SMS
                                                                </div>
                                                                <div class="p-0 b-b w-full"></div>
                                                                <div class="col-4 px-2 b-r">
                                                                    ${{ scope.row.yesterday_usage.twilio_usage.tollfree_pn | fixRounding(1) }}
                                                                </div>
                                                                <div class="col-4 px-2 b-r">
                                                                    ${{ scope.row.yesterday_usage.twilio_usage.tollfree_min | fixRounding(2) }}
                                                                </div>
                                                                <div class="col-4 px-2">
                                                                    ${{ scope.row.yesterday_usage.twilio_usage.tollfree_sms | fixRounding(2) }}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <span>ShortCode Usage:</span>
                                                            <div class="row no-gutter b-a">
                                                                <div class="col-4 px-2 font-weight-bold">
                                                                    SMS
                                                                </div>
                                                                <div class="col-3 px-2 b-r font-weight-bold">
                                                                    MMS
                                                                </div>
                                                                <div class="p-0 b-b w-full"></div>
                                                                <div class="col-3 px-2 b-r">
                                                                    ${{ scope.row.yesterday_usage.twilio_usage.short_code_sms | fixRounding(2) }}
                                                                </div>
                                                                <div class="col-4 px-2">
                                                                    ${{ scope.row.yesterday_usage.twilio_usage.short_code_mms | fixRounding(2) }}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="col-md-6"
                                                 v-if="!auth.user.profile.is_reseller">
                                                <strong class="text-greyish">
                                                    Last 4 log entries:
                                                </strong>
                                                <br>
                                                <ul>
                                                    <li v-for="log in $options.filters.lastItems(scope.row.logs, 4)?.slice().reverse()">
                                                        {{ log.body }}
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </template>
                                </el-table-column>
                                <el-table-column
                                    prop="company.id"
                                    label="Account"
                                    min-width="100"
                                    sortable>
                                    <template v-slot="scope">
                                        <p class="mb-1"
                                           v-if="scope.row">
                                            {{ scope.row.name }} - #{{ scope.row.id }}
                                        </p>
                                        <p class="mb-1"
                                           v-if="scope.row.cycle_start_date">
                                            Latest Subscription: {{ scope.row.cycle_start_date | fixDateTime }}
                                        </p>

                                        <p class="mb-1"
                                           v-if="!auth.user.profile.is_reseller">
                                            Billing Cycle: {{ scope.row.current_cycle }}
                                        </p>

                                        <p class="mb-1"
                                           v-if="scope.row.creator_admin">
                                            Account Creator: <b>{{ scope.row.creator_admin.name }}</b>
                                        </p>

                                        <p class="mb-1"
                                           v-if="scope.row">
                                            Created: {{ scope.row.created_at | fixDateTime }}
                                        </p>

                                        <template v-if="!auth.user.profile.is_reseller">
                                            <p class="mb-1"
                                               v-if="scope.row.hubspot_account_owner">
                                                HubSpot Sales Rep Owner: <b>{{ scope.row.hubspot_account_owner }}</b>
                                            </p>

                                            <p class="mb-1"
                                               v-if="scope.row.hubspot_csm_owner">
                                                HubSpot CSM Owner: <b>{{ scope.row.hubspot_csm_owner }}</b>
                                            </p>

                                            <p class="mb-1"
                                               v-if="scope.row.hubspot_business_segment">
                                                HubSpot CftSegment: <b>{{ scope.row.hubspot_business_segment }}</b>
                                            </p>

                                            <p class="mb-1"
                                               v-if="scope.row.hubspot_latest_deal_amount">
                                                HubSpot Latest Deal Amount: <b>${{ scope.row.hubspot_latest_deal_amount | fixRounding(0) }}</b>
                                            </p>

                                            <p class="mb-1"
                                               v-if="scope.row.hubspot_latest_deal_owner">
                                                HubSpot Latest Deal Owner: <b>{{ scope.row.hubspot_latest_deal_owner }}</b>
                                            </p>
                                            <p class="mb-1"
                                               v-if="scope.row.legal_country">
                                                Legal country (compliance): <b>{{ scope.row.legal_country }}</b>
                                            </p>
                                            <p class="mb-1"
                                               v-if="scope.row.country">
                                                Account country: <b>{{ scope.row.country }}</b>
                                            </p>
                                            <p class="mb-1"
                                               v-if="scope.row.pre_signup">
                                                PreSignup Referrer: <b>{{ scope.row.pre_signup.referrer }}</b>
                                            </p>
                                        </template>

                                        <div class="b-a p-2 mb-1">
                                            <span><b>Account Source</b></span>
                                            <br>
                                            <span>Created by: </span><strong>{{
                                                getCompanyCreatedSource(scope.row)
                                            }}</strong>
                                        </div>

                                        <p class="mb-1"
                                           v-if="scope.row.deleted_at">
                                            <b class="label danger">Account is deleted</b>
                                        </p>

                                        <p class="mb-1"
                                           v-if="scope.row.purging_status == Company.PURGING_STATUS_ONGOING">
                                            <b class="label danger">Account purging is ongoing</b>
                                        </p>

                                        <p class="mb-1"
                                           v-if="scope.row.purging_status == Company.PURGING_STATUS_FAILED">
                                            <b class="label danger">Account purging is failed</b>
                                        </p>

                                        <p class="mb-1"
                                           v-if="!scope.row.enabled">
                                            <b class="label danger text-left"
                                               v-if="isReseller">
                                                <p class="mb-1">
                                                    Suspended by Aloware
                                                </p>
                                                <span>{{ scope.row.suspended_at | fixDateTime }}</span>
                                            </b>
                                            <b class="label danger text-left"
                                               v-else-if="scope.row.suspender">
                                                <p class="mb-1">
                                                    Suspended by {{ company.suspender.name }} - #{{ company.suspender.id }}
                                                </p>
                                                <span>{{ scope.row.suspended_at | fixDateTime }}</span>
                                            </b>
                                            <b class="label danger text-left"
                                               v-else>
                                                <p class="mb-1">
                                                    Suspended
                                                </p>
                                                <span>{{ scope.row.suspended_at | fixDateTime }}</span>
                                            </b>
                                        </p>

                                        <el-divider
                                            v-if="scope.row.registered_customer_profile || scope.row.registered_a2p_trust_bundle || scope.row.registered_a2p_brand || scope.row.registered_stir_shaken || scope.row.registered_address || scope.row.submitted_compliance"
                                            class="mt-3 mb-3">
                                        </el-divider>

                                        <p class="mb-1"
                                           v-if="scope.row.a2p_registration_type">
                                            <b class="label orange">{{ Company.A2P_REGISTRATION_NAMES[scope.row.a2p_registration_type] }}</b>
                                        </p>
                                        <p class="mb-1"
                                           v-if="scope.row.submitted_compliance">
                                            <b class="label green-900">Submitted Compliance Form</b>
                                        </p>
                                        <p class="mb-1"
                                           v-if="scope.row.registered_address">
                                            <b class="label indigo-900">Address Registered</b>
                                        </p>
                                        <p class="mb-1"
                                           v-if="scope.row.registered_customer_profile">
                                            <b class="label teal-900">Customer Profile Registered</b>
                                        </p>
                                        <p class="mb-1"
                                           v-if="scope.row.registered_a2p_trust_bundle">
                                            <b class="label red-900">A2P 10DLC Bundle Registered</b>
                                        </p>
                                        <p class="mb-1"
                                           v-if="scope.row.registered_a2p_brand">
                                            <b class="label deep-purple-900">A2P 10DLC Brand Registered</b>
                                        </p>
                                        <p class="mb-1"
                                           v-if="scope.row.registered_stir_shaken">
                                            <b class="label pink-900">STIR/SHAKEN Registered</b>
                                        </p>
                                        <p class="mb-1"
                                           v-if="scope.row.registered_voice_integrity">
                                            <b class="label blue-900">Voice Integrity Registered</b>
                                        </p>

                                        <template v-if="scope.row.primary_carrier && scope.row.primary_carrier.sid">
                                            <el-divider class="mt-3 mb-3"></el-divider>
                                            <div>
                                                <p class="mb-1 _400">
                                                    Twilio subaccount sid: <b>{{ scope.row.primary_carrier.sid }}</b>
                                                </p>
                                            </div>
                                        </template>
                                    </template>
                                </el-table-column>
                                <el-table-column
                                    label="Billing"
                                    min-width="140">
                                    <template v-slot="scope">
                                        <template v-if="scope.row">
                                            <p class="mb-1"
                                               v-if="scope.row.plan">
                                                Plans: {{ scope.row.plan.name }}
                                                (${{ scope.row.plan.price.toLocaleString() | fixRounding(0) }}
                                                {{ scope.row.plan.type === 1 ? 'flat fee' : 'per user' }})
                                            </p>
                                            <p class="mb-1">
                                                <b class="label deep-orange">Auto Collection: {{ scope.row.chargebee_auto_collection ? 'On' : 'Off' }}</b>
                                            </p>
                                            <p class="mb-1">
                                                <b class="label yellow">Payment Grace Period: {{ scope.row.payment_grace_period }} days</b>
                                            </p>
                                            <p class="mb-1"
                                               v-if="scope.row.card_added">
                                                <b class="label purple">Credit Card Exists</b>
                                            </p>
                                            <p class="mb-1"
                                               v-else>
                                                <b class="label pink">Credit Card Not Found</b>
                                            </p>
                                            <p class="mb-1"
                                               v-if="scope.row.is_high_volume">
                                                <b class="label green">High Volume Account</b>
                                            </p>
                                            <p class="mb-1"
                                               v-if="scope.row.is_reseller">
                                                <b class="label orange">Partner Account</b>
                                            </p>
                                            <p class="mb-1"
                                               v-if="scope.row.reseller_id && scope.row.reseller_company">
                                                <b class="label blue">Partner Resold Account</b>
                                                <br>
                                                <span class="text-blue">
                                                    Partner Account: #{{ scope.row.reseller_company.id }} - {{ scope.row.reseller_company.name }}
                                                </span>
                                            </p>

                                            <div class="my-2 b-a p-2"
                                                 v-if="[TrialStatus.TRIAL_STATUS_EXPIRED, TrialStatus.TRIAL_STATUS_ACTIVE, TrialStatus.TRIAL_STATUS_PURGE_ELIGIBLE].includes(scope.row.trial_status)">
                                                <strong class="mb-2"
                                                        v-if="scope.row.trial_status !== TrialStatus.TRIAL_STATUS_PURGE_ELIGIBLE"
                                                        :style="'color:' + status_colors[scope.row.trial_status]">
                                                    {{ trial_status_names[scope.row.trial_status] }}
                                                </strong>
                                                <strong class="mb-2"
                                                        v-else
                                                        :style="'color:' + status_colors[TrialStatus.TRIAL_STATUS_EXPIRED]">
                                                    Trial Expired
                                                </strong>

                                                <div>
                                                    <strong>Started at:</strong>
                                                    <span v-if="scope.row.trial_start_at">{{
                                                            scope.row.trial_start_at | fixDateTime
                                                        }}</span>
                                                    <span v-else>-</span>
                                                </div>
                                                <div>
                                                    <strong>Ends at:</strong>
                                                    <span v-if="scope.row.trial_ends_at">{{ scope.row.trial_ends_at | fixDateTime }}</span>
                                                    <span v-else>-</span>
                                                </div>
                                                <div v-if="scope.row.trial_remaining_days > 0">
                                                    <strong>
                                                        Status:
                                                    </strong>
                                                    {{ scope.row.trial_remaining_days }}
                                                    {{ scope.row.trial_remaining_days > 1 ? 'days' : 'day' }} remaining
                                                </div>
                                                <div v-if="scope.row.trial_remaining_days <= 0">
                                                    <strong>Status:</strong> Expired
                                                </div>

                                                <template v-if="scope.row.trial_status === TrialStatus.TRIAL_STATUS_PURGE_ELIGIBLE">
                                                    <hr>
                                                    <strong :style="'color:' + status_colors[scope.row.trial_status]">
                                                        {{ trial_status_names[scope.row.trial_status] }}
                                                    </strong>
                                                    <strong>From:</strong>
                                                    <span v-if="scope.row.trial_ends_at">{{ getTrialPurgeDate(scope.row) | fixDateTime }}</span>
                                                    <span v-else>-</span>
                                                </template>
                                            </div>

                                            <div class="b-a p-2 mb-1"
                                                 v-if="discount_data && show_chargebee_info">
                                                <span><b>Chargebee Info</b></span>
                                                <br>
                                                <span>Status: </span><strong :style="getSubscriptionStyle(company.subscription.status)">{{
                                                    getSubscriptionStatus(company.subscription.status)
                                                }}</strong>
                                                <br>
                                                <span>List plan desc: {{ scope.row.plan.description }}</span>
                                                <br>
                                                <span>Discount percentage: {{ discount_data.percentage_discount_per_user }}</span>
                                                <br>
                                                <span>Discount per user: ${{ discount_data.discount_per_user }}</span>
                                                <br>
                                                <span>Cost per user after discount: ${{ discount_data.cost_per_user_after_discount }}</span>
                                                <br>
                                                <span>Payment terms: {{ discount_data.payment_terms }}</span>
                                                <br>
                                                <span>Total after discount: ${{ discount_data.total_after_discount }}</span>
                                                <hr>
                                                <template v-if="scope.row.plan">
                                                    <span>Unlimited Inbound:</span>
                                                    <span :class="yes_no.color_class[scope.row.plan.unlimited_inbound]">
                                                        {{ yes_no.label[scope.row.plan.unlimited_inbound] }}
                                                    </span>
                                                    |
                                                    <span>Unlimited Outbound:</span>
                                                    <span :class="yes_no.color_class[scope.row.plan.unlimited_outbound]">
                                                        {{ yes_no.label[scope.row.plan.unlimited_outbound] }}
                                                    </span>
                                                </template>
                                                <template v-else>
                                                    <b class="text-danger">This account doesn't have a plan notify BillingOps immediately!</b>
                                                </template>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <b class="text-danger">Deleted Company</b>
                                        </template>
                                    </template>
                                </el-table-column>
                                <el-table-column
                                    prop="credits"
                                    label="Usage"
                                    min-width="120">
                                    <template v-slot="scope">
                                        <p class="mb-1 _600"
                                           :class="[ scope.row.credits < 0 ? 'text-danger' : '' ]">
                                            <span>Credits: ${{ scope.row.credits | fixRounding(2) }}</span>
                                        </p>
                                        <p class="mb-1 _600">
                                            <span>Max Negative Balance: ${{ scope.row.max_negative_balance | fixRounding(2) }} </span>
                                        </p>
                                        <p class="mb-1">
                                            <span>Current User Count: {{ scope.row.used_users }}</span>
                                        </p>
                                        <p class="mb-1"
                                           v-if="perSeatPlanUseCase">
                                            <span>Current License Count: {{ scope.row.purchased_licenses }} </span>
                                        </p>
                                        <p class="mb-1"
                                           v-if="perSeatPlanUseCase">
                                            <span>Minimum License Count: {{ scope.row.min_user_seat }} </span>
                                        </p>
                                        <p class="mb-1">
                                            <span>Admin Last Login: {{ scope.row.billing_admins ? scope.row.billing_admins.last_login : '' | fixDateTime }}</span>
                                        </p>
                                        <p class="mb-1"
                                           v-if="scope.row.yesterday_usage && scope.row.yesterday_usage.twilio_usage">
                                            <span>Yesterday's Usage: ${{ scope.row.yesterday_usage.twilio_usage.sum | fixRounding(2) }}</span>
                                        </p>
                                        <p class="mb-1">
                                            <el-link href="/account?tab=usage"
                                                     target="_blank">
                                                To see usage, impersonate the account and navigate to Account >> Usage
                                            </el-link>
                                        </p>

                                        <div v-if="scope.row.diagnosis && scope.row.diagnosis.length > 0 && !auth.user.profile.is_reseller"
                                             class="alert alert-warning alert-aloware">
                                            <strong>
                                                <i class="fa fa-exclamation-circle"></i>
                                                Account Diagnosis
                                            </strong>
                                            <ul>
                                                <li v-for="item in scope.row.diagnosis">{{ item }}</li>
                                            </ul>
                                        </div>
                                    </template>
                                </el-table-column>
                                <el-table-column
                                    label="Controls & Features"
                                    min-width="120">
                                    <template v-slot="scope">
                                        <el-form label-position="left">
                                            <div :class="['w-100', { 'half-opaq': !isCarrierOpsAdmin && !isSuperAdmin }]">
                                                <el-switch
                                                    v-model="scope.row.use_new_engine"
                                                    active-color="#00a344"
                                                    :disabled="(!isCarrierOpsAdmin && !isSuperAdmin) || scope.row.task_router_setup_status === 'setting_up'"
                                                    @change="updateFeature(scope.row, 'use_new_engine')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    Use New Telephony Engine
                                                </label>
                                                <span v-if="scope.row.task_router_setup_status === 'setting_up'" class="text-warning ml-1">
                                                    <i class="fa fa-spinner fa-spin"></i> Setting up...
                                                </span>
                                                <span v-if="scope.row.task_router_setup_status === 'failed'" class="text-danger ml-1">
                                                    <i class="fa fa-exclamation-triangle"></i> Setup failed
                                                </span>
                                                <el-tooltip class="item"
                                                            effect="dark"
                                                            placement="bottom">
                                                    <div slot="content">Whether to use the new engine for the account. <br>
                                                        When enabled, all the incoming numbers will be attached to the new engine. <br>
                                                        When disabled, the old engine will be used.
                                                    </div>
                                                    <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                </el-tooltip>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdminOrReseller }]">
                                                <el-switch
                                                    v-model="scope.row.bulk_10dlc_call_only_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdminOrReseller"
                                                    @change="updateFeature(scope.row, 'bulk_10dlc_call_only')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    {{ set_10dlc_as_calling_only_label }}
                                                </label>
                                                <el-tooltip class="item"
                                                            effect="dark"
                                                            placement="bottom">
                                                    <div slot="content">Setting lines with 10DLC from US and Canada as 'Calling Only' disables outbound SMS and MMS but still allows inbound messages.
                                                        <br>
                                                        Both inbound and outbound voice calls remain enabled.
                                                    </div>
                                                    <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                </el-tooltip>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdminOrReseller }]">
                                                <el-switch
                                                    v-model="scope.row.bulk_tollfree_call_only_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdminOrReseller"
                                                    @change="updateFeature(scope.row, 'bulk_tollfree_call_only')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    {{ set_toll_free_as_calling_only_label }}
                                                </label>
                                                <el-tooltip class="item"
                                                            effect="dark"
                                                            placement="bottom">
                                                    <div slot="content">Setting lines with Toll-Free Numbers as 'Calling Only' disables outbound SMS and MMS but still allows inbound messages. <br>
                                                        Both inbound and outbound voice calls remain enabled.
                                                    </div>
                                                    <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                </el-tooltip>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdmin }]">
                                                <el-switch
                                                    v-model="scope.row.automated_international_traffic_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdmin"
                                                    @change="updateFeature(scope.row, 'automated_international_traffic_enabled')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    Allow Automated International Traffic
                                                    <el-popover
                                                        placement="bottom"
                                                        width="320"
                                                        trigger="hover">
                                                        <div>
                                                            <p><b>Default behavior</b>: OFF</p>
                                                            <p>Automated international traffic (non-human-initiated communications such as bulk, API, integrations, sequences, etc.) is blocked by default to reduce the risk of traffic abuse and unintended international pumping.</p>
                                                            <p><b>When OFF:</b> Only one-to-one (1:1) international traffic is allowed and it is based on the account’s assigned international traffic tier.</p>
                                                            <p><b>When ON:</b> The automated international traffic is allowed and it is within the account’s international traffic tier.</p>
                                                            <p><b>For CSMs:</b> Written client authorization is required before enabling this setting. Share the
                                                            <a href="https://2qfyk.share.hsforms.com/2rX8CvAGOR86fQwr1L07nxw" class="text-blue font-weight-bold" target="_blank">International Calling and Messaging Approval Form</a> to request approval.</p>
                                                        </div>
                                                        <i slot="reference" class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                    </el-popover>
                                                </label>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdmin }]"
                                                 v-if="!isReseller">
                                                <el-switch
                                                    v-model="scope.row.send_messages_via_hubspot_workflows_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdmin"
                                                    @change="updateFeature(scope.row, 'send_messages_via_hubspot_workflows_enabled')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    {{ allow_messaging_from_hubspot_label }}
                                                    <el-tooltip class="item"
                                                                effect="dark"
                                                                placement="bottom">
                                                        <div slot="content">
                                                            Enable or disable the ability for customers to send SMS via HubSpot (HS) workflows. <br>
                                                            Default is enabled for all accounts but can be disabled for identified bad actors to prevent spam.
                                                        </div>
                                                        <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                    </el-tooltip>
                                                </label>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdmin }]">
                                                <el-switch
                                                    v-model="scope.row.undnc_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdmin"
                                                    @change="updateFeature(scope.row, 'undnc_enabled')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    UnDNC Ability
                                                </label>
                                            </div>

                                            <hr>

                                            <template v-if="!auth.user.profile.is_reseller">

                                                <div :class="['w-100', { 'half-opaq': !isSuperAdminOrReseller }]">
                                                    <el-switch
                                                        v-model="scope.row.crm_integrations_enabled"
                                                        active-color="#00a344"
                                                        :disabled="!isSuperAdminOrReseller"
                                                        @change="updateFeature(scope.row, 'crm_integrations')">
                                                    </el-switch>
                                                    <label class="el-form-item__label ml-2 float-none">
                                                        CRM Integrations
                                                    </label>
                                                </div>

                                                <div :class="['w-100', { 'half-opaq': !isSuperAdminOrReseller }]">
                                                    <el-switch
                                                        v-model="scope.row.lrn_lookup_enabled"
                                                        active-color="#00a344"
                                                        :disabled="!isSuperAdminOrReseller"
                                                        @change="updateFeature(scope.row, 'lrn_lookup_enabled')">
                                                    </el-switch>
                                                    <label class="el-form-item__label ml-2 float-none">
                                                        LRN Lookup Service
                                                    </label>
                                                </div>

                                                <div :class="['w-100', { 'half-opaq': !isSuperAdminOrReseller }]">
                                                    <el-switch
                                                        v-model="scope.row.show_toll_free_verification_banner"
                                                        active-color="#00a344"
                                                        :disabled="!isSuperAdminOrReseller"
                                                        @change="updateFeature(scope.row, 'show_toll_free_verification_banner')">
                                                    </el-switch>
                                                    <label class="el-form-item__label ml-2 float-none">
                                                        Force Toll-Free Verification
                                                    </label>
                                                </div>

                                                <div :class="['w-100', { 'half-opaq': !isSuperAdminOrReseller }]">
                                                    <el-switch
                                                        v-model="scope.row.force_opt_out_phrase"
                                                        active-color="#00a344"
                                                        :disabled="!isSuperAdminOrReseller"
                                                        @change="updateFeature(scope.row, 'force_opt_out_phrase')">
                                                    </el-switch>
                                                    <label class="el-form-item__label ml-2 float-none">
                                                        Force Opt-Out Phrase
                                                    </label>
                                                </div>

                                                <div :class="['w-100', { 'half-opaq': !isSuperAdminOrReseller }]"
                                                     v-if="app_env !== 'production'">
                                                    <el-switch
                                                        v-model="scope.row.network_logs_enabled"
                                                        active-color="#00a344"
                                                        :disabled="!isSuperAdminOrReseller"
                                                        @change="updateFeature(scope.row, 'network_logs')">
                                                    </el-switch>
                                                    <label class="el-form-item__label ml-2 float-none">
                                                        Enable Network Logs
                                                    </label>
                                                </div>

                                                <hr>
                                            </template>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdminOrReseller }]">
                                                <el-switch
                                                    v-model="scope.row.bulk_sms_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdminOrReseller"
                                                    @change="updateFeature(scope.row, 'bulk_sms')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    Bulk SMS
                                                    <el-tooltip class="item"
                                                                effect="dark"
                                                                placement="bottom">
                                                        <div slot="content">Enables the broadcasting tool and SMS broadcasting. Needs compliance and defined use case. Only CSMs should enable this.
                                                        </div>
                                                        <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                    </el-tooltip>
                                                </label>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdminOrReseller }]">
                                                <el-switch
                                                    v-model="scope.row.bulk_rvm_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdminOrReseller"
                                                    @change="updateFeature(scope.row, 'bulk_rvm')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    Bulk RVM
                                                    <el-tooltip class="item"
                                                                effect="dark"
                                                                placement="bottom">
                                                        <div slot="content">Enables broadcasting tool and ringless voicemail. Needs permission from management on defined use case</div>
                                                        <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                    </el-tooltip>
                                                </label>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdminOrReseller }]">
                                                <el-switch
                                                    v-model="scope.row.auto_dialer_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdminOrReseller"
                                                    @change="updateFeature(scope.row, 'auto_dialer')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    PowerDialer
                                                    <el-tooltip class="item"
                                                                effect="dark"
                                                                placement="bottom">
                                                        <div slot="content">Comes with xPro plan only and NOT available on uPro/iPro. CSMs must upsell the plan to xPro for use.</div>
                                                        <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                    </el-tooltip>
                                                </label>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdminOrReseller }]">
                                                <el-switch
                                                    v-model="scope.row.automation_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdminOrReseller"
                                                    @change="updateFeature(scope.row, 'automation')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    Sequence
                                                    <el-tooltip class="item"
                                                                effect="dark"
                                                                placement="bottom">
                                                        <div slot="content">Comes with the uPro plan and NOT available for iPro plans</div>
                                                        <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                    </el-tooltip>
                                                </label>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdminOrReseller }]">
                                                <el-switch
                                                    v-model="scope.row.progressive_dialer_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdminOrReseller"
                                                    @change="updateFeature(scope.row, 'progressive_dialer')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    Progressive Dialer
                                                    <el-tooltip class="item"
                                                                effect="dark"
                                                                placement="bottom">
                                                        <div slot="content">Enables the Progressive Dialer in Sequences</div>
                                                        <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                    </el-tooltip>
                                                </label>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdmin }]"
                                                 v-if="!isReseller">
                                                <el-switch
                                                    v-model="scope.row.transcription_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdmin"
                                                    @change="updateFeature(scope.row, 'transcription_enabled')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    AloAi Voice Analytics
                                                    <el-tooltip class="item"
                                                                effect="dark"
                                                                placement="bottom">
                                                        <div slot="content">Enables call analytics & transcriptions on the dashboard. Must be purchased as an upsell separately by the CSM.
                                                        </div>
                                                        <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                    </el-tooltip>
                                                </label>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdmin }]"
                                                 v-if="!isReseller">
                                                <el-switch
                                                    v-model="scope.row.aloai_text_agents_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdmin"
                                                    @change="updateFeature(scope.row, 'aloai_text_agents_enabled')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    AloAi Text Agents
                                                    <el-tooltip class="item"
                                                                effect="dark"
                                                                placement="bottom">
                                                        <div slot="content">Enables AI inbound/outbound text agents for this company.
                                                        </div>
                                                        <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                    </el-tooltip>
                                                </label>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdmin }]"
                                                 v-if="!isReseller">
                                                <el-switch
                                                    v-model="scope.row.aloai_voice_inbound_agents_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdmin"
                                                    @change="updateFeature(scope.row, 'aloai_voice_inbound_agents_enabled')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    AloAi Inbound Voice Agents
                                                    <el-tooltip class="item"
                                                                effect="dark"
                                                                placement="bottom">
                                                        <div slot="content">Enables AI inbound voice agents for this company.
                                                        </div>
                                                        <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                    </el-tooltip>
                                                </label>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdmin }]"
                                                 v-if="!isReseller">
                                                <el-switch
                                                    v-model="scope.row.aloai_voice_outbound_agents_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdmin"
                                                    @change="updateFeature(scope.row, 'aloai_voice_outbound_agents_enabled')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    AloAi Outbound Voice Agents
                                                    <el-tooltip class="item"
                                                                effect="dark"
                                                                placement="bottom">
                                                        <div slot="content">Enables AI outbound voice agents for this company.
                                                        </div>
                                                        <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                    </el-tooltip>
                                                </label>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdmin }]"
                                                 v-if="!isReseller">
                                                <el-switch
                                                    v-model="scope.row.aloai_missed_call_handler_feature_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdmin"
                                                    @change="updateFeature(scope.row, 'aloai_missed_call_handler_feature_enabled')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    AI Call Rescue Feature
                                                    <el-tooltip class="item"
                                                                effect="dark"
                                                                placement="bottom">
                                                        <div slot="content">Enables the Call Rescue feature for this company.
                                                        </div>
                                                        <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                    </el-tooltip>
                                                </label>
                                            </div>



                                            <div :class="['w-100', { 'half-opaq': !isSuperAdmin }]"
                                                 v-if="!isReseller">
                                                <el-switch
                                                    v-model="scope.row.team_inbox_line_management_enhancements"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdmin || loading_dependent_feature"
                                                    @change="handleTeamInboxLineManagementEnhancementsChange(scope.row)">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    Team Inbox Line Management
                                                    <el-tooltip class="item"
                                                                effect="dark"
                                                                placement="bottom">
                                                        <div slot="content">
                                                            Enables Team Inbox Line Management enhancements for this company.
                                                        </div>
                                                        <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                    </el-tooltip>
                                                </label>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdmin }]"
                                                 v-if="!isReseller">
                                                <el-switch
                                                    v-model="scope.row.team_inbox_line_management_banners"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdmin"
                                                    @change="updateFeature(scope.row, 'team_inbox_line_management_banners')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    Team Inbox Line Management Banners
                                                    <el-tooltip class="item"
                                                                effect="dark"
                                                                placement="bottom">
                                                        <div slot="content">
                                                            Enables Team Inbox Line Management banners for this company.
                                                        </div>
                                                        <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                    </el-tooltip>
                                                </label>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdmin }]"
                                                 v-if="!isReseller">
                                                <el-switch
                                                    v-model="scope.row.rcs_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdmin"
                                                    @change="updateFeature(scope.row, 'rcs_enabled')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    RCS
                                                    <el-tooltip class="item"
                                                                effect="dark"
                                                                placement="bottom">
                                                        <div slot="content">Enables RCS messaging for this company.
                                                        </div>
                                                        <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                    </el-tooltip>
                                                </label>
                                            </div>

                                            <div :class="['w-100', { 'half-opaq': !isSuperAdmin }]"
                                                 v-if="!isReseller">
                                                <el-switch
                                                    v-model="scope.row.mcp_enabled"
                                                    active-color="#00a344"
                                                    :disabled="!isSuperAdmin"
                                                    @change="updateFeature(scope.row, 'mcp_enabled')">
                                                </el-switch>
                                                <label class="el-form-item__label ml-2 float-none">
                                                    MCP Access
                                                    <el-tooltip class="item"
                                                                effect="dark"
                                                                placement="bottom">
                                                        <div slot="content">Enables MCP (Model Context Protocol) access for this company. Disable to block all MCP tool usage.
                                                        </div>
                                                        <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                                                    </el-tooltip>
                                                </label>
                                            </div>
                                        </el-form>
                                    </template>
                                </el-table-column>
                                <el-table-column
                                    label="Operation"
                                    width="200">
                                    <template v-slot="scope">
                                        <el-popover placement="left"
                                                    width="500"
                                                    trigger="click"
                                                    v-if="scope.row && !auth.user.profile.is_reseller"
                                                    :ref="'changeRate-' + scope.row.id"
                                                    @hide="resetChangeRate">
                                            <el-form ref="change_rate_form"
                                                     :model="change_rate">
                                                <el-form-item label="Rate:"
                                                              class="mb-2">
                                                    <el-select v-model="change_rate.rate_id"
                                                               placeholder="Select Rate"
                                                               class="w-full"
                                                               filterable>
                                                        <el-option v-for="item in rates"
                                                                   :key="item.id"
                                                                   :label="item.name"
                                                                   :value="item.id">
                                                            <span class="pull-left">{{ item.name }}</span>
                                                        </el-option>
                                                    </el-select>
                                                </el-form-item>
                                                <el-button type="success"
                                                           class="w-full"
                                                           :disabled="!change_rate.rate_id"
                                                           :loading="loading_change_rate"
                                                           @click.prevent="changeRate(scope.row)">
                                                    Change
                                                </el-button>
                                            </el-form>
                                            <button slot="reference"
                                                    class="btn btn-sm btn-block blue mb-2"
                                                    :disabled="loading_rates">
                                                Change Rate
                                            </button>
                                        </el-popover>
                                        <el-popover placement="left"
                                                    width="500"
                                                    trigger="click"
                                                    :ref="'changeMps-' + scope.row.id">
                                            <el-form>
                                                <el-form-item label="Campaign:"
                                                              class="mb-2">
                                                    <el-select v-model="change_max_mps.campaign_id"
                                                               placeholder="Select Campaign"
                                                               class="w-full"
                                                               filterable>
                                                        <el-option v-for="campaign in campaigns"
                                                                   :key="campaign.id"
                                                                   :label="campaign.name"
                                                                   :value="campaign.id">
                                                            <span class="pull-left">{{ campaign.name }}</span>
                                                        </el-option>
                                                    </el-select>
                                                </el-form-item>
                                                <el-form-item label="Max MPS:"
                                                              class="mb-2">
                                                    <el-select v-model="change_max_mps.max_mps"
                                                               placeholder="Select Max MPS"
                                                               class="w-full"
                                                               filterable>
                                                        <el-option v-for="item in mpsOptions"
                                                                   :key="item.label"
                                                                   :label="item.label"
                                                                   :value="item.value">
                                                            <span class="pull-left">{{ item.label }}</span>
                                                        </el-option>
                                                    </el-select>
                                                </el-form-item>
                                                <el-button type="success"
                                                           class="w-full"
                                                           :disabled="!change_max_mps.campaign_id || !change_max_mps.max_mps"
                                                           @click.prevent="changeMps(scope.row)">
                                                    Change
                                                </el-button>
                                            </el-form>
                                            <button slot="reference"
                                                    class="btn btn-sm btn-block orange text-white mb-2">
                                                Change Campaign MPS
                                            </button>
                                        </el-popover>

                                        <el-popover placement="left"
                                                    width="500"
                                                    trigger="click"
                                                    v-if="scope.row && auth.user.profile.is_reseller && isCustomReseller"
                                                    :ref="'changePlan-' + scope.row.id"
                                                    @hide="resetChangePlan">
                                            <el-form ref="change_plan_form"
                                                     :model="change_plan">
                                                <el-form-item label="Plan:"
                                                              class="mb-2">
                                                    <modgenius-plan-selector ref="modgeniusSelector"
                                                                             v-model="change_plan.plan_id"/>
                                                </el-form-item>
                                                <el-button type="success"
                                                           class="w-full"
                                                           :disabled="!change_plan.plan_id"
                                                           :loading="loading_change_plan"
                                                           @click.prevent="changePlan(scope.row)">
                                                    Change
                                                </el-button>
                                            </el-form>
                                            <button slot="reference"
                                                    class="btn btn-sm btn-block pink-900 mb-2">
                                                Change Plan
                                            </button>
                                            <a class="text-decoration-underline el-link el-link--primary mt-1"
                                               href="https://alwr.io/GkGz3a"
                                               target="_blank">
                                                See More Details
                                            </a>
                                        </el-popover>

                                        <el-popover placement="left"
                                                    width="500"
                                                    trigger="click">
                                            <el-form ref="auto_recharge_form"
                                                     class="p-2">
                                                <div class="row">
                                                    <div class="col-12">
                                                        <div class="switch-label mb-3">
                                                            <el-switch v-model="auto_recharge"
                                                                       active-color="#00a344"
                                                                       @change="checkAutoRecharge">
                                                            </el-switch>
                                                            <label>Auto Recharge</label>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="row"
                                                     v-show="auto_recharge">
                                                    <div class="col-5">
                                                        For amount
                                                    </div>
                                                    <div class="col-7">
                                                        <el-select placeholder="Select"
                                                                   v-model="recharge_for_amount"
                                                                   class="pull-right">
                                                            <el-option
                                                                v-for="item in recharge_for_amount_options"
                                                                :key="item.value"
                                                                :label="item.label"
                                                                :value="item.value">
                                                            </el-option>
                                                        </el-select>
                                                    </div>
                                                </div>
                                                <div class="row"
                                                     v-show="auto_recharge">
                                                    <div class="col-5">
                                                        When Balance is below
                                                    </div>
                                                    <div class="col-7">
                                                        <el-select placeholder="Select"
                                                                   v-model="auto_recharge_threshold"
                                                                   class="pull-right">
                                                            <el-option
                                                                v-for="item in auto_recharge_threshold_options"
                                                                :key="item.value"
                                                                :label="item.label"
                                                                :value="item.value">
                                                            </el-option>
                                                        </el-select>
                                                    </div>
                                                </div>
                                                <div class="row mt-3"
                                                     v-show="auto_recharge">
                                                    <div class="col-12">
                                                        <el-button type="success"
                                                                   :disabled="loading_auto_recharge"
                                                                   @click="changeAutoRecharge"
                                                                   class="pull-right">
                                                            <i class="material-icons loader pull-right"
                                                               v-show="loading_auto_recharge">&#xE863;</i>
                                                            Save Details
                                                        </el-button>
                                                    </div>
                                                </div>
                                            </el-form>
                                            <button slot="reference"
                                                    class="btn btn-sm btn-block green-600 mb-2">
                                                Auto Recharge Settings
                                            </button>
                                        </el-popover>
                                        <el-popover placement="left"
                                                    width="270"
                                                    trigger="click"
                                                    v-if="scope.row"
                                                    v-model="dialog_max_negative_balance_visible"
                                                    @show="showMaxNegativeBalance(scope.row)">
                                            <el-form class="edit-configurations"
                                                     @submit.prevent.native="preventSubmit">
                                                <el-form-item class="mb-2 max-negative-balance"
                                                              prop="max_negative_balance">
                                                    <div class="md-form-group py-0">
                                                        <label class="position-relative my-0">Max Negative Balance</label>
                                                        <div class="d-flex w-100">
                                                            <span class="font-120 text-dark font-weight-bold mr-2 align-self-center">$</span>
                                                            <el-input-number placeholder="-0.00"
                                                                             class="w-100"
                                                                             size="small"
                                                                             ref="max_negative_balance"
                                                                             :precision="2"
                                                                             :controls="false"
                                                                             v-model="configurations_edit.max_negative_balance"
                                                                             @change="validateMaxNegativeBalance">
                                                            </el-input-number>
                                                        </div>
                                                        <div class="text-center">
                                                            <small class="small d-block text-danger"
                                                                   v-if="invalid_max_negative_balance">
                                                                Value must be less than or equal to 0.
                                                            </small>
                                                            <small class="small d-block"
                                                                   :class="{ 'mt--1': invalid_max_negative_balance }">
                                                                <span class="font-weight-bold">NOTE:</span> Decimals will get rounded off.
                                                            </small>
                                                        </div>
                                                        <div class="d-inline">
                                                            <button class="btn btn-xs btn-success pull-right"
                                                                    tabindex="1"
                                                                    @click.prevent="updateMaxNegativeBalance(configurations_edit.max_negative_balance)">
                                                                Save
                                                            </button>
                                                            <button class="btn btn-xs _400 btn-secondary pull-right mr-2"
                                                                    @click="hideMaxNegativeBalanceDialog">
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                </el-form-item>
                                            </el-form>
                                            <button slot="reference"
                                                    class="btn btn-sm btn-block purple-400 mb-2"
                                                    v-if="!auth.user.profile.is_reseller"
                                                    @click="showMaxNegativeBalance(scope.row)">
                                                Change Max Negative Bal.
                                            </button>
                                        </el-popover>
                                        <el-popover placement="left"
                                                    width="270"
                                                    trigger="click"
                                                    v-model="dialog_minimum_license_count_visible"
                                                    @show="showUserMinSeat(scope.row)"
                                                    :class="[{ 'half-opaq': !enableMinCountSetup }]">
                                            <el-form class="edit-configurations"
                                                     @submit.prevent.native="preventSubmit">
                                                <el-form-item class="mb-2"
                                                              prop="min_user_seat">
                                                    <div class="md-form-group py-0">
                                                        <label class="position-relative my-0">Minimum License Count</label>
                                                        <div class="d-flex w-100">
                                                            <el-input-number class="w-100"
                                                                             size="small"
                                                                             ref="min_user_seat"
                                                                             :min="1"
                                                                             v-model="min_user_seat"
                                                                             @change="validateMinimumLicenseCount">
                                                            </el-input-number>
                                                        </div>
                                                        <div class="text-center">
                                                            <small class="small d-block text-danger mb-2 mt-1 lh-1"
                                                                   v-if="invalid_minimum_license_count">
                                                                {{ min_user_seat_validation_message }}
                                                            </small>
                                                            <small class="small d-block"
                                                                   :class="{ 'mt--1': invalid_minimum_license_count }">
                                                                <span class="font-weight-bold">NOTE:</span> Smallest acceptable value is 1.
                                                            </small>
                                                        </div>
                                                        <div class="d-inline">
                                                            <button class="btn btn-xs btn-success pull-right"
                                                                    tabindex="1"
                                                                    @click.prevent="updateMinimumLicenseCount(min_user_seat)">
                                                                Save
                                                            </button>
                                                            <button class="btn btn-xs _400 btn-secondary pull-right mr-2"
                                                                    @click="hideUserMinSeatDialog">
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                </el-form-item>
                                            </el-form>
                                            <button slot="reference"
                                                    class="btn btn-sm btn-block cyan-400 mb-2"
                                                    :disabled="!enableMinCountSetup"
                                                    v-if="!auth.user.profile.is_reseller"
                                                    @click="showUserMinSeat(scope.row)">
                                                Minimum License Count
                                            </button>
                                        </el-popover>
                                        <el-popover :ref="'addLog-' + scope.row.id"
                                                    placement="left"
                                                    width="400"
                                                    trigger="click"
                                                    v-if="scope.row">
                                            <el-form ref="add_log_form"
                                                     :model="add_log">
                                                <el-form-item label="Log:"
                                                              class="mb-2">
                                                    <el-input type="textarea"
                                                              :autosize="{ minRows: 2, maxRows: 4}"
                                                              placeholder="Type here"
                                                              v-model="add_log.body">
                                                    </el-input>
                                                </el-form-item>
                                                <el-button type="success"
                                                           class="w-full"
                                                           :disabled="!add_log.body"
                                                           :loading="loading_add_log"
                                                           @click.prevent="addLog(scope.row)">
                                                    Add
                                                </el-button>
                                            </el-form>
                                            <button slot="reference"
                                                    class="btn btn-sm btn-block bluish mb-2"
                                                    v-if="!auth.user.profile.is_reseller">
                                                Add Log
                                            </button>
                                        </el-popover>
                                        <button v-if="scope.row && !scope.row.deleted_at && !auth.user.profile.is_reseller"
                                                class="btn btn-sm btn-block yellow mb-2"
                                                @click="showLogs(scope.row.logs)">
                                            Logs
                                        </button>

                                        <button v-if="!auth.user.profile.is_reseller"
                                                :class="['btn btn-sm btn-block mb-2', scope.row.mobile_logs_enabled ? 'red' : 'blue']"
                                                @click="updateFeature(scope.row, 'mobile_logs_enabled')">
                                            {{ scope.row.mobile_logs_enabled ? 'Disable 📱 Logs' : 'Enable 📱 Logs' }}
                                        </button>

                                        <div class="pos-rlt mb-2">
                                            <el-popover v-model="configurations[scope.$index]"
                                                        placement="right-start"
                                                        trigger="manual"
                                                        width="270"
                                                        :ref="'configurations-editor-' + scope.row.id"
                                                        @show="onShowConfiguration(scope.row)"
                                                        @hide="beforeCloseConfiguration">
                                                <el-form class="edit-configurations"
                                                         ref="edit_configurations"
                                                         label-width="auto"
                                                         label-position="top"
                                                         :model="configurations_edit"
                                                         @submit.prevent.native="preventSubmit">
                                                    <div style="text-align: right; margin: 0">
                                                        <div class="row">
                                                            <div class="col-12">
                                                                <div class="mb-3">
                                                                    <div class="text-left">
                                                                        <el-form-item class="mb-0 mt-2 hubspot-company-id"
                                                                                      prop="hubspot_company_id">
                                                                            <div class="md-form-group pb-0">
                                                                                <label class="mb-0 lh-1">HubSpot Company ID</label>
                                                                                <el-input class="md-input"
                                                                                          ref="hubspot_company_id"
                                                                                          clearable
                                                                                          v-model="configurations_edit.hubspot_company_id"
                                                                                          @input="preValidateForm('edit_configurations')">
                                                                                </el-input>
                                                                            </div>
                                                                        </el-form-item>

                                                                        <el-form-item class="mb-0 mt-2 hubspot-deal-id"
                                                                                      prop="hubspot_deal_id">
                                                                            <div class="md-form-group pb-0">
                                                                                <label class="mb-0 lh-1">HubSpot Deal ID</label>
                                                                                <el-input class="md-input"
                                                                                          ref="hubspot_deal_id"
                                                                                          clearable
                                                                                          v-model="configurations_edit.hubspot_deal_id"
                                                                                          @input="preValidateForm('edit_configurations')">
                                                                                </el-input>
                                                                            </div>
                                                                        </el-form-item>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div class="col-12 mt-2">
                                                                <button class="btn btn-xs _400 greenish pull-right"
                                                                        :disabled="configurations_saving || !validated"
                                                                        @click="updateConfigurations(scope.row.id, scope.$index)">
                                                                    <i class="material-icons loader"
                                                                       v-show="configurations_saving">&#xE863;</i>
                                                                    <span v-show="!configurations_saving">Save</span>
                                                                </button>
                                                                <button class="btn btn-xs _400 btn-secondary pull-right mr-2"
                                                                        :disabled="configurations_saving"
                                                                        @click="hideConfigurationDialog(scope.$index)">
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </el-form>
                                                <button slot="reference"
                                                        class="btn btn-sm btn-block text-light"
                                                        style="background-color: #FA7820;"
                                                        v-if="!auth.user.profile.is_reseller && isSuperAdmin"
                                                        @click="showConfigurationDialog(scope.$index)">
                                                    Configurations
                                                </button>
                                            </el-popover>
                                        </div>

                                        <a v-if="scope.row.hubspot_company_id && !auth.user.profile.is_reseller"
                                           :href="getHubspotCompanyLink(scope.row)"
                                           class="btn orange btn-block btn-sm text-white"
                                           target="_blank">
                                            <i class="fa fa-link"></i> Open HS Company
                                        </a>

                                        <a v-if="scope.row.hubspot_deal_id && !auth.user.profile.is_reseller"
                                           :href="getHubspotDealLink(scope.row)"
                                           class="btn orange btn-block btn-sm text-white"
                                           target="_blank">
                                            <i class="fa fa-link"></i> Open HS Deal
                                        </a>

                                        <a v-if="scope.row.subscription && scope.row.subscription.id && !auth.user.profile.is_reseller"
                                           :href="getChargebeeSubscriptionLink(scope.row)"
                                           target="_blank"
                                           class="btn purple btn-block btn-sm text-white">
                                            <i class="fa fa-link"></i> Open Chargebee
                                        </a>

                                        <button v-if="scope.row.subscription && scope.row.subscription.id && !auth.user.profile.is_reseller"
                                                :disabled="braintreeLoading"
                                                class="btn black btn-block btn-sm text-white"
                                                @click="openBraintree(scope.row)">
                                            <i class="fa"
                                               :class="{'fa-spin fa-spinner': braintreeLoading, 'fa-link': !braintreeLoading}"></i>
                                            {{ braintreeLoading ? 'Loading...' : 'Open Braintree' }}
                                        </button>

                                        <button v-if="scope.row.is_trial && !scope.row.card_added  && !auth.user.profile.is_reseller && isSuperAdmin"
                                                :disabled="paymentRequirementOverrideLoading"
                                                class="btn purple btn-block btn-sm text-white"
                                                @click="overridePaymentRequirement(scope.row)">
                                            <i class="fa"
                                            :class="{'fa-spin fa-spinner': paymentRequirementOverrideLoading}"></i>
                                            {{ paymentRequirementOverrideLoading ? 'Overriding...' : 'Override Payment Req.' }}
                                        </button>

                                        <manage-reseller :company="scope.row"
                                                         v-if="scope.row && allowedToManageCompanyResellers">
                                        </manage-reseller>

                                        <local-presence-purchase :company="scope.row"
                                                                 v-if="scope.row && !auth.user.profile.is_reseller"
                                                                 @purchased="handleLocalPresencePurchased">
                                        </local-presence-purchase>

                                        <local-presence-upgrade :company="scope.row"
                                                                v-if="scope.row && hasMajorCitiesLocalPresence"
                                                                @upgraded="handleLocalPresencePurchased">
                                        </local-presence-upgrade>

                                        <local-presence-cancelation :company="scope.row"
                                                                    v-if="scope.row && !auth.user.profile.is_reseller"
                                                                    @canceled="handleLocalPresencePurchased">
                                        </local-presence-cancelation>



                                        <!-- AloAi Call Limits Settings -->
                                        <el-popover placement="left"
                                                    width="350"
                                                    trigger="click"
                                                    v-if="!isReseller && (scope.row.aloai_voice_inbound_agents_enabled || scope.row.aloai_voice_outbound_agents_enabled)"
                                                    v-model="aloai_throttle_dialog_visible"
                                                    @show="showAloAiThrottleSettings(scope.row)">
                                            <div class="aloai-throttle-settings">
                                                <h6 class="mb-3">AloAi Call Limits Configuration</h6>
                                                <el-form ref="aloai_throttle_form"
                                                         :model="aloai_throttle_settings">
                                                    <el-form-item class="mb-3"
                                                                  prop="aloai_max_concurrent_outbound_calls">
                                                        <div class="md-form-group py-0">
                                                            <label class="position-relative my-0">Max Concurrent Outbound Calls</label>
                                                            <div class="d-flex w-100">
                                                                <el-input-number class="w-100"
                                                                                 size="small"
                                                                                 :min="1"
                                                                                 :max="20"
                                                                                 v-model="aloai_throttle_settings.aloai_max_concurrent_outbound_calls">
                                                                </el-input-number>
                                                            </div>
                                                            <small class="small d-block text-muted">
                                                                Maximum simultaneous outbound calls
                                                            </small>
                                                        </div>
                                                    </el-form-item>

                                                    <!--
                                                    <el-form-item class="mb-3"
                                                                  prop="aloai_max_concurrent_inbound_calls">
                                                        <div class="md-form-group py-0">
                                                            <label class="position-relative my-0">Max Concurrent Inbound Calls</label>
                                                            <div class="d-flex w-100">
                                                                <el-input-number class="w-100"
                                                                                 size="small"
                                                                                 :min="1"
                                                                                 :max="20"
                                                                                 v-model="aloai_throttle_settings.aloai_max_concurrent_inbound_calls">
                                                                </el-input-number>
                                                            </div>
                                                            <small class="small d-block text-muted">
                                                                Maximum simultaneous inbound calls
                                                            </small>
                                                        </div>
                                                    </el-form-item>
                                                    -->

                                                    <div class="text-right">
                                                        <button type="button"
                                                                class="btn btn-xs btn-secondary mr-2"
                                                                @click="hideAloAiThrottleDialog">
                                                            Cancel
                                                        </button>
                                                        <button type="button"
                                                                class="btn btn-xs btn-success"
                                                                :disabled="aloai_throttle_saving"
                                                                @click="updateAloAiThrottleSettings()">
                                                            <i class="material-icons loader"
                                                               v-show="aloai_throttle_saving">&#xE863;</i>
                                                            <span v-show="!aloai_throttle_saving">Save</span>
                                                        </button>
                                                    </div>
                                                </el-form>
                                            </div>
                                            <button slot="reference"
                                                    type="button"
                                                    class="btn btn-sm btn-block btn-info mt-2"
                                                    :disabled="!isSuperAdmin">
                                                AloAi Call Limits
                                                <el-tooltip class="item ml-1"
                                                            effect="dark"
                                                            placement="left">
                                                    <div slot="content">
                                                        Configure concurrent call limits for AloAi voice agents.<br/>
                                                        Current - Outbound: {{ scope.row.aloai_max_concurrent_outbound_calls}} calls<br/>
                                                        <!-- Current - Inbound: {{ scope.row.aloai_max_concurrent_inbound_calls}} calls -->
                                                    </div>
                                                    <i class="material-icons text-lg cursor-pointer">info_outline</i>
                                                </el-tooltip>
                                            </button>
                                        </el-popover>

                                        <el-popover placement="left"
                                                    width="500"
                                                    trigger="click"
                                                    @show="onShowInternationalSupport(scope.row)"
                                                    v-if="scope.row">
                                            <el-form>
                                                <el-form-item label="Local and International Traffic Tiers:"
                                                              class="mb-2">
                                                    <el-select v-model="international_tier"
                                                               placeholder="Select Tier"
                                                               class="w-full"
                                                               filterable>
                                                        <el-option label="Local Traffic Tier 1"
                                                                   :value="Company.INTERNATIONAL_TIER_1">
                                                            <span class="pull-left">Local Traffic Tier 1</span>
                                                        </el-option>
                                                        <el-option label="International Traffic Tier 2"
                                                                   :value="Company.INTERNATIONAL_TIER_2">
                                                            <span class="pull-left">International Traffic Tier 2</span>
                                                        </el-option>
                                                        <el-option label="International Traffic Tier 3"
                                                                   :value="Company.INTERNATIONAL_TIER_3">
                                                            <span class="pull-left">International Traffic Tier 3</span>
                                                        </el-option>
                                                        <el-option label="International Traffic Tier 4"
                                                                   :value="Company.INTERNATIONAL_TIER_4">
                                                            <span class="pull-left">International Traffic Tier 4</span>
                                                            <el-tooltip class="item ml-1"
                                                                        effect="dark"
                                                                        placement="bottom">
                                                                <div slot="content">
                                                                    <span v-if="!isInternationalAdmin">
                                                                        This will create a request that will be reviewed by the international admin team. Note is required.
                                                                    </span>
                                                                    <span v-else>
                                                                        A note is required when changing to Tier 4.
                                                                    </span>
                                                                </div>
                                                                <i class="material-icons text-lg cursor-pointer">info_outline</i>
                                                            </el-tooltip>
                                                        </el-option>
                                                    </el-select>
                                                </el-form-item>
                                                <el-form-item label="Note (Required for Tier 4):"
                                                              v-if="isTier4Selected"
                                                              class="mb-2">
                                                    <el-input
                                                        v-model="international_tier_request_note"
                                                        type="textarea"
                                                        :rows="3"
                                                        placeholder="Please provide a note explaining the reason for this Tier 4 change request"
                                                        maxlength="1000"
                                                        show-word-limit>
                                                    </el-input>
                                                </el-form-item>
                                                <el-button type="success"
                                                           class="w-full"
                                                           :loading="loading_international_tier"
                                                           :disabled="isTier4NoteDisabled"
                                                           @click.prevent="changeInternationalTier(scope.row)">
                                                    Change
                                                </el-button>
                                            </el-form>
                                            <button slot="reference"
                                                    class="btn btn-sm btn-block purple-400 mt-2"
                                                    :disabled="loading_international_tier">
                                                International Traffic
                                                <el-tooltip class="item ml-1"
                                                            effect="dark"
                                                            placement="left">
                                                    <div slot="content">
                                                        Set the traffic tier (Local or International).<br/>
                                                        The account country does not define traffic type.
                                                    </div>
                                                    <i class="material-icons text-lg cursor-pointer">info_outline</i>
                                                </el-tooltip>
                                            </button>
                                        </el-popover>

                                        <button class="btn btn-sm btn-block danger mt-2"
                                                v-if="scope.row.enabled && !isReseller"
                                                @click="suspendCompany(scope.row)">
                                            Suspend
                                        </button>

                                        <button class="btn btn-sm btn-block new-blue mt-2"
                                                v-if="!scope.row.enabled && canUnsuspendAccount(scope.row)"
                                                @click="unsuspendCompany(scope.row)">
                                            Unsuspend
                                        </button>

                                        <div v-if="canManageAccounts && !company.enabled">
                                            <el-tooltip class="item"
                                                        effect="dark"
                                                        placement="bottom"
                                                        v-if="company.purging_status == Company.PURGING_STATUS_NOT_USED">
                                                <div slot="content">This will mark this account as ready to purge at night</div>
                                                <button class="btn btn-sm btn-block danger mt-2"
                                                        :disabled="loading_initiate_purge"
                                                        @click="initiatePurge(company)">
                                                    Schedule Purge
                                                </button>
                                            </el-tooltip>

                                            <button class="btn btn-sm btn-block danger mt-2"
                                                    v-if="company.purging_status != Company.PURGING_STATUS_NOT_USED"
                                                    :disabled="company.purging_status >= Company.PURGING_STATUS_ONGOING || loading_cancel_initiate_purge"
                                                    @click="cancelInitiatePurge(company)">
                                                Cancel Purge
                                            </button>
                                        </div>
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>
                    </el-tab-pane>
                    <el-tab-pane name="company-user-list"
                                 label="Users"
                                 v-if="!company.is_reseller">
                        <div class="row">
                            <div class="col-2">
                                <el-input
                                    v-model="search"
                                    @input="applySearch"
                                    placeholder="Type to apply search..."
                                    prefix-icon="el-icon-search"
                                    clearable>
                                </el-input>
                            </div>
                            <div class="pull-right mr-2 no-select">
                                <el-select
                                    v-model="user_type_filter"
                                    @change="changedUserTypeFilter"
                                    class="no-select"
                                    placeholder="Select"
                                    default-first-option
                                    filterable>
                                    <el-option
                                        :key="1"
                                        label="Users"
                                        value="Users">
                                    </el-option>
                                    <el-option
                                        :key="6"
                                        label="Billing Admins"
                                        value="Billing Admin">
                                    </el-option>
                                    <el-option
                                        :key="2"
                                        label="Admins"
                                        value="Company Admin">
                                    </el-option>
                                    <el-option
                                        :key="3"
                                        label="Agents"
                                        value="Company Agent">
                                    </el-option>
                                    <el-option
                                        :key="4"
                                        label="Reporters"
                                        value="Company Reporter Access">
                                    </el-option>
                                    <el-option
                                        :key="5"
                                        label="Extensions"
                                        value="Extensions">
                                    </el-option>
                                    <el-option
                                        :key="7"
                                        label="Supervisor"
                                        value="Company Supervisor">
                                    </el-option>
                                </el-select>
                            </div>

                            <div class="ml-auto">
                                <el-pagination
                                    layout="sizes, prev, pager, next"
                                    :current-page.sync="user_pagination.page"
                                    :page-sizes="[10, 20, 50, 100]"
                                    :page-size.sync="user_pagination.size"
                                    :total="user_pagination.total"
                                    @size-change="paginateUsers(1)"
                                    @current-change="paginateUsers">
                                </el-pagination>
                            </div>
                        </div>
                        <el-table ref="company-user-list"
                                  v-loading="loading_company_users"
                                  :data="company_users"
                                  class="w-full"
                                  stripe>
                            <el-table-column label="ID"
                                             prop="id"
                                             width="100">
                                <template v-slot="scope">
                                    {{ scope.row.id }}
                                </template>
                            </el-table-column>
                            <el-table-column label="Other accounts"
                                             prop="accounts"
                                             width="200">
                                <template v-slot="scope">
                                    <div v-if="scope.row.user_accesses && scope.row.user_accesses.length > 0">
                                        <b :class="[user.company.enabled ? 'label green' : 'label red']"
                                           v-for="user in scope.row.user_accesses"
                                           v-if="company.id !== user.company_id">
                                            {{ user.company.name }}
                                        </b>
                                    </div>
                                    <b v-else>
                                        -
                                    </b>
                                </template>
                            </el-table-column>
                            <el-table-column label="Name"
                                             prop="name">
                                <template v-slot="scope">
                                    {{ scope.row.name }}
                                </template>
                            </el-table-column>
                            <el-table-column label="Description"
                                             prop="description">
                                <template v-slot="scope">
                                    <span v-if="scope.row.description">{{ scope.row.description }}</span>
                                    <span v-else>-</span>
                                </template>
                            </el-table-column>
                            <el-table-column label="Email"
                                             prop="email">
                                <template v-slot="scope">
                                    {{ scope.row.email }}
                                </template>
                            </el-table-column>
                            <el-table-column label="Status"
                                             prop="name">
                                <template v-slot="scope">
                                    {{ scope.row.status }}
                                </template>
                            </el-table-column>
                            <el-table-column label="Role"
                                             prop="name">
                                <template v-slot="scope">
                                    <div class="row"
                                         v-for="role in scope.row.roles">
                                        <div class="col-12">
                                            <span class="text-ellipsis"
                                                  :title="role.name">
                                                {{ role.name }}
                                            </span>
                                        </div>
                                    </div>
                                </template>
                            </el-table-column>
                            <el-table-column label="Created at"
                                             prop="created_at">
                                <template v-slot="scope">
                                    {{ scope.row.created_at | fixFullDateLocal }}
                                </template>
                            </el-table-column>
                            <el-table-column label="Last login"
                                             prop="last_login">
                                <template v-slot="scope">
                                    {{ scope.row.last_login | fixFullDateLocal }}
                                </template>
                            </el-table-column>
                            <el-table-column label="Deleted at"
                                             prop="deleted_at">
                                <template v-slot="scope">
                                    {{ scope.row.deleted_at | fixFullDateLocal }}
                                </template>
                            </el-table-column>
                            <el-table-column label="Action"
                                             width="150">
                                <template v-slot="scope">
                                    <el-tooltip
                                        effect="dark"
                                        :content="getUserImpersonateDisabledReason(scope.row)"
                                        placement="top"
                                        :disabled="!getUserImpersonateDisabledReason(scope.row)">
                                        <button class="btn btn-sm btn-block purple-800"
                                                :disabled="!!getUserImpersonateDisabledReason(scope.row)"
                                                @click="impersonate(scope.row.id)">
                                            Impersonate
                                        </button>
                                    </el-tooltip>
                                    <button class="btn btn-sm btn-block blue-800"
                                            @click="showAssignAccountDialog(scope.row)">
                                        Assign Account
                                    </button>
                                    <button class="btn btn-sm btn-block indigo-700"
                                            v-if="scope.row.enabled && scope.row.deleted_at == null"
                                            @click="showRolesDialog(scope.row)">
                                        Manage Roles
                                    </button>
                                    <button class="btn btn-sm btn-block warning mb-2"
                                            @click.prevent="resendWelcomeEmail(scope.row.id)"
                                            v-if="!scope.row.email_verified_at">
                                        Send Welcome Email
                                    </button>
                                    <button class="btn btn-sm btn-block blue mb-2"
                                            @click.prevent="verifyUser(scope.row)"
                                            v-if="!scope.row.email_verified_at || !scope.row.phone_number_verified_at">
                                        Verify User
                                    </button>
                                    <button class="btn btn-sm btn-block deep-orange-500"
                                            @click="resetPassword(scope.row)">
                                        Reset Password
                                    </button>
                                    <button class="btn btn-sm btn-block"
                                            :class="scope.row.mobile_logs_enabled ? 'red' : 'blue'"
                                            @click="toggleUserMobileLogs(scope.row)">
                                        {{ scope.row.mobile_logs_enabled ? 'Disable 📱 Logs' : 'Enable 📱 Logs' }}
                                    </button>
                                    <button class="btn btn-sm btn-block purple-A400"
                                            v-if="scope.row.enabled && scope.row.deleted_at == null"
                                            @click="suspendUser(scope.row)">
                                        Suspend
                                    </button>
                                    <button class="btn btn-sm btn-block btn-info"
                                            v-if="!scope.row.enabled && scope.row.deleted_at == null"
                                            @click="unsuspendUser(scope.row)">
                                        Unsuspend
                                    </button>
                                    <button class="btn btn-sm btn-block greenish"
                                            v-if="scope.row.deleted_at"
                                            @click="restoreUser(scope.row)">
                                        Restore User
                                    </button>
                                    <el-tooltip 
                                        effect="dark"
                                        content="Your company's current plan does not allow deleting users"
                                        placement="top"
                                        :disabled="!isJoinNationwideReseller">
                                        <button class="btn btn-sm btn-block danger"
                                                v-if="!scope.row.deleted_at"
                                                :disabled="isJoinNationwideReseller"
                                                @click="deleteUserRemote(scope.row.id)">
                                            Delete
                                        </button>
                                    </el-tooltip>
                                </template>
                            </el-table-column>
                        </el-table>
                    </el-tab-pane>
                    <el-tab-pane name="compliance"
                                 label="Compliance"
                                 v-if="!company.is_reseller">
                        <compliance-tab :company="company"
                                        :initial-filter="complianceFilter"
                                        @enabled-reregistration="company.submitted_compliance = false">
                        </compliance-tab>
                    </el-tab-pane>
                    <el-tab-pane name="kyc"
                                 label="KYC"
                                 v-if="shouldShowKycTab && !company.is_reseller">
                        <div class="kyc-review">
                            <kyc-account-tab :company="company"/>
                        </div>
                    </el-tab-pane>
                    <el-tab-pane name="ask-aloai"
                                 label="Ask AloAi">
                        <div class="p-4">
                            <div class="chat-container">
                                <div class="chat-header d-flex justify-content-between align-items-center mb-3">
                                    <h5 class="mb-0">Conversation History</h5>
                                    <button class="btn btn-sm btn-outline-danger"
                                            @click="clearChat">
                                        <i class="fas fa-trash-alt"></i> Clear Chat
                                    </button>
                                </div>
                                <div class="conversation-container"
                                     ref="conversationContainer">
                                    <div v-for="(message, index) in conversation_history"
                                         :key="index"
                                         class="message-wrapper"
                                         :ref="'message-' + index"
                                         :class="{'message-outbound': message.isUser}">
                                        <div class="message-content">
                                            <div class="message-bubble"
                                                 :class="{'message-bubble-user': message.isUser}">
                                                <div class="message-text markdown-content"
                                                     v-html="message.response"></div>
                                            </div>
                                            <small class="message-timestamp text-muted">{{ message.timestamp | fixDateTime }}</small>
                                        </div>
                                    </div>
                                    <div v-if="aloai_loading"
                                         class="message-wrapper mb-3">
                                        <div class="message-content">
                                            <div class="message-bubble">
                                                <div class="typing-indicator">
                                                    <span></span>
                                                    <span></span>
                                                    <span></span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="input-container">
                                    <div class="recommendations mt-3">
                                        <div class="recommendations-label text-muted mb-2">Recommended Questions:</div>
                                        <div class="recommendations-list">
                                            <el-tag
                                                v-for="(prompt, index) in recommendedPrompts"
                                                :key="index"
                                                class="recommendation-tag mr-2 mb-2"
                                                @click="useRecommendation(prompt)"
                                                effect="plain">
                                                {{ prompt }}
                                            </el-tag>
                                        </div>
                                    </div>
                                    <el-input
                                        v-model="aloai_prompt"
                                        type="textarea"
                                        :rows="4"
                                        placeholder="Ask AloAi anything about this account... (Press Ctrl/Cmd + Enter for new line)"
                                        class="mb-3"
                                        @keydown.enter.native.prevent="handleEnter"
                                        @keydown.ctrl.enter.native="newline"
                                        @keydown.meta.enter.native="newline">
                                    </el-input>
                                    <div class="d-flex justify-content-end">
                                        <el-button type="primary"
                                                   @click="askAloAi"
                                                   :loading="aloai_loading">
                                            Ask AloAi
                                        </el-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>
                </el-tabs>
            </template>
        </div>
        <el-dialog ref="companyLogsModal"
                   title="Account Logs"
                   :visible.sync="showLogsVisible">
            <el-table empty-text="No logs"
                      ref="companyLogsTable"
                      :data="logs"
                      :default-sort="{prop: 'created_at', order: 'descending'}"
                      fit
                      stripe>
                <el-table-column prop="body"
                                 label="Log">
                </el-table-column>
                <el-table-column prop="created_at"
                                 label="Created At"
                                 width="180"
                                 sortable>
                    <template v-slot="props">
                        {{ props.row.created_at | fixDateTime }}
                    </template>
                </el-table-column>
            </el-table>
        </el-dialog>
        <user-deletion-modal
            :visible.sync="showDeletionModal"
            is-super-admin
            :user-id-to-delete="userIdToDelete"
            :available-users="activeUsers"
            :company="company"
            @user-deleted="handleUserDeleted"
        />
    </div>
</template>

<script>
import _ from 'lodash'
import cloneDeep from 'lodash/cloneDeep'
import MarkdownIt from 'markdown-it'
import moment from 'moment'
import UserDeletionModal from '../../user/components/user-deletion-modal.vue'
import * as Plan from '../../user/constants/plan'
import { STARTUP_PACKAGES } from "../../user/constants/startup_packages"
import { setCookie } from "../../utils/functions"
import auth from '../auth'
import KycAccountTab from '../components/support-tools/kyc-account-tab'
import { AUTO_RECHARGE_VALUES } from "../constants/auto-recharge"
import * as Company from '../constants/company'
import * as LocalPresenceOptions from '../constants/local-presence-options'
import { PRE_SIGNUP_SUBMISSION_TYPES } from '../constants/pre-signup'
import * as TrialStatus from '../constants/trial-account-status'
import { acl_mixin, addons_mixin, form_validation_mixin, prompt_mixin, subscription_style_mixin } from '../mixins'
import ComplianceTab from './compliance/compliance-tab'
import ModgeniusPlanSelector from './modgenius/modgenius-plan-selector.vue'
import LocalPresenceCancelation from './widgets/local-presence-cancelation'
import LocalPresencePurchase from './widgets/local-presence-purchase'
import LocalPresenceUpgrade from './widgets/local-presence-upgrade'
import ManageReseller from './widgets/manage-reseller'

const _user_form = () => {
    return {
        id: null,
        first_name: '',
        last_name: '',
        phone_number: '',
        email: '',
        enabled: true
    }
}

export default {
    components: {
        LocalPresencePurchase,
        LocalPresenceCancelation,
        LocalPresenceUpgrade,
        ComplianceTab,
        ModgeniusPlanSelector,
        KycAccountTab,
        ManageReseller,
        UserDeletionModal
    },

    mixins: [
        form_validation_mixin,
        addons_mixin,
        subscription_style_mixin,
        acl_mixin,
        prompt_mixin,
    ],

    data() {
        return {
            auth: auth,
            app_env: localStorage.getItem('app_env'),
            visible_row_id: null,
            search_text: null,
            dev_user_id: null,
            demo_user_id: null,
            loading: false,
            md: new MarkdownIt({
                html: true,
                breaks: true,
                linkify: true
            }),
            add_log: {
                body: null
            },
            loading_add_log: false,
            change_rate: {
                rate_id: null
            },
            change_plan: {
                plan_id: null,
            },
            change_max_mps: {
                campaign_id: null,
                max_mps: null,
            },
            campaigns: [],
            loading_international_tier: false,
            international_tier_request_note: '',
            loading_change_rate: false,
            loading_change_plan: false,
            loading_plans: false,
            loading_rates: false,
            loading_company: false,
            loading_company_users: false,
            loading_company_account: false,
            logs: null,
            showLogsVisible: false,
            company_summary: [],
            users: [],
            plans: [],
            rates: [],
            yes_no: {
                color_class: {
                    0: 'text-danger font-weight-bold',
                    1: 'text-success font-weight-bold'
                },
                label: {
                    0: 'No',
                    1: 'Yes'
                }
            },
            yesterday_balance_empty: {
                sum: 0,
                local_pn: 0,
                local_min: 0,
                local_mms: 0,
                local_sms: 0,
                tollfree_pn: 0,
                tollfree_min: 0,
                tollfree_sms: 0,
                short_code_sms: 0,
                short_code_mms: 0,
            },
            admin_selector_disabled: false,
            configurations_saving: false,
            configurations: [],
            configurations_edit: {
                max_negative_balance: 0,
                hubspot_company_id: null,
                hubspot_deal_id: null,
            },
            registering_address: false,
            active_tab: 'company-card',
            company: {},
            trial_status_names: {
                [TrialStatus.TRIAL_STATUS_PURGE_ELIGIBLE]: 'Trial Purge Eligible',
                [TrialStatus.TRIAL_STATUS_NOT_STARTED]: 'Not Started',
                [TrialStatus.TRIAL_STATUS_EXPIRED]: 'Trial Expired',
                [TrialStatus.TRIAL_STATUS_ACTIVE]: 'In Trial',
                [TrialStatus.TRIAL_STATUS_SUCCESS]: 'Success'
            },
            status_colors: {
                [TrialStatus.TRIAL_STATUS_PURGE_ELIGIBLE]: '#e600ff',
                [TrialStatus.TRIAL_STATUS_NOT_STARTED]: '#607D8B',
                [TrialStatus.TRIAL_STATUS_EXPIRED]: '#FF9800',
                [TrialStatus.TRIAL_STATUS_ACTIVE]: '#00ccff',
                [TrialStatus.TRIAL_STATUS_SUCCESS]: '#4CAF50'
            },
            selected_plan: null,
            company_users: [],
            all_company_users: [],
            company_account: {},
            user_form: _user_form(),
            search: '',
            user_type_filter: 'Billing Admin',
            assign_account: {
                loading: false,
                show_dialog: false,
                user_id: null,
                search: '',
                assigned: [],
                accounts: [],
                search_current_page: 1,
                search_per_page: 10,
                filters: {
                    search: '',
                    status: null,
                    reseller_id: null,
                    abandoned_type: null,
                    assignable_accounts: true,
                    exclude_company: this.$route.params.id,
                },
            },
            assign_roles: {
                loading: false,
                show_dialog: false,
                user_id: null,
                assigned: null,
            },
            loading_dependent_feature: false,
            min_user_seat: 1,
            invalid_minimum_license_count: false,
            min_user_seat_validation_message: '',
            dialog_minimum_license_count_visible: false,
            invalid_max_negative_balance: false,
            dialog_max_negative_balance_visible: false,
            max_negative_balance_saving: false,
            user_pagination: {
                page: 1,
                size: 20,
                total: 0
            },
            recharge_for_amount_options: AUTO_RECHARGE_VALUES,
            auto_recharge_threshold_options: AUTO_RECHARGE_VALUES,
            auto_recharge: _.get(this.company, 'auto_recharge', null) !== null ? this.company.auto_recharge : null,
            loading_auto_recharge: false,
            recharge_for_amount: _.get(this.company, 'recharge_for_amount', null) !== null ? this.company.recharge_for_amount : null,
            auto_recharge_threshold: _.get(this.company, 'auto_recharge_threshold', null) !== null ? this.company.auto_recharge_threshold : null,
            international_tier: _.get(this.company, 'international_tier', null) !== null ? this.company.international_tier : 1,
            discount_data: null,
            show_chargebee_info: false,
            custom_resellers_ids: [2132],
            loading_initiate_purge: false,
            loading_cancel_initiate_purge: false,
            braintreeLoading: false,
            paymentRequirementOverrideLoading: false,
            allow_outbound_sms_mms_label: 'Allow Outbound SMS/MMS',
            allow_messaging_from_hubspot_label: 'Allow Messaging from HS Workflow',
            set_toll_free_as_calling_only_label: 'Set Toll-Free Lines as Calling Only',
            set_10dlc_as_calling_only_label: 'Set 10DLC Lines as Calling Only',
            aloai_prompt: '',
            aloai_response: '',
            aloai_loading: false,
            conversation_id: null,
            conversation_history: [],
            recommendedPrompts: [
                "Does this account has a high risk of fraud?",
                "Analyze user activities for the past 30 days",
                "Create a table of user activities of the past 30 days sorted based on total activity count descending",
                "Analyze users additions, deletions and suspensions for the past 30 days",
                "Determine if an account's automated SMS usage has been consistent over the past 30 days and identify any patterns"
            ],
            LocalPresenceOptions,
            Company,
            TrialStatus,
            Plan,
            cancelTokenSource: null,
            aloai_throttle_dialog_visible: false,
            aloai_throttle_settings: {
                aloai_max_concurrent_outbound_calls: 2,
                //aloai_max_concurrent_inbound_calls: 5
            },
            aloai_throttle_saving: false,
            showDeletionModal: false,
            userIdToDelete: null
        }
    },

    computed: {
        mpsOptions() {
            let mps_list = [0.05, 0.1, 0.25, 0.5, 1, 2, 3, 4, 6, 8, 10]
            let list = []
            mps_list.forEach((element) => {
                list.push({
                    label: element * 60 * 60 + ' per hour',
                    value: element
                })
            })
            return list
        },

        isTier4Selected() {
            return this.international_tier === Company.INTERNATIONAL_TIER_4
        },

        isTier4NoteDisabled() {
            return this.isTier4Selected && !this.international_tier_request_note.trim()
        },

        complianceFilter() {
            return this.$route.query.filter
        },

        canManageAccounts() {
            return this.isSuperAdmin || this.isRevOpsAdmin
        },

        isJoinNationwideReseller() {
            if (!this.company || !this.company.reseller_id) {
                return false
            }

            return this.company.reseller_capabilities.join_nationwide_reseller
        },

        customRoles() {
            return [
                {
                    id: 'supervisor',
                    name: 'Supervisor',
                    roles: [
                        'Company Supervisor'
                    ]
                },
                {
                    id: 'reporter',
                    name: 'Reporter',
                    roles: [
                        'Company Agent',
                        'Company Reporter Access'
                    ]
                },
                {
                    id: 'billing_admin',
                    name: 'Billing Admin',
                    roles: [
                        'Company Admin',
                        'Billing Admin'
                    ]
                },
                {
                    id: 'agent',
                    name: 'Agent',
                    roles: [
                        'Company Agent'
                    ]
                },
                {
                    id: 'admin',
                    name: 'Admin',
                    roles: [
                        'Company Admin'
                    ]
                },
            ]
        },

        hasMajorCitiesLocalPresence() {
            // loop through companies local presence packages
            // if any of them has major cities, return true
            return _.some(this.company.local_presence_packages, (local_presence_packages) => {
                return local_presence_packages.type === LocalPresenceOptions.US_MAJOR_CITIES
            })
        },

        isCustomReseller() {
            return this.custom_resellers_ids.includes(this.company.reseller_id)
        },

        perSeatPlanUseCase() {
            return ['iPro', 'uPro', 'xPro'].includes(this.company.plan.use_case)
        },

        enableMinCountSetup() {
            const is_rev_ops_admin = this.isRevOpsAdmin
            const is_sales_admin = this.isSalesAdmin
            const is_super_admin = this.isSuperAdmin
            const is_sales_rep = this.isSalesRep

            if (!is_rev_ops_admin && !is_sales_admin && !is_super_admin && !is_sales_rep) {
                return false
            }

            // A Sales Rep is only allowed to set the 1st min_seat_count value
            if (is_sales_rep && !is_rev_ops_admin && !is_sales_admin && !is_super_admin && this.company.was_min_user_seat_edited) {
                return false
            }

            return this.perSeatPlanUseCase
        },

        shouldShowKycTab() {
            return !this.isReseller || this.isModgenius
        },

        activeUsers() {
            return this.all_company_users.filter(user => !user.deleted_at)
        }
    },

    created() {
        this.dev_user_id = localStorage.getItem('dev_user_id')
        this.demo_user_id = localStorage.getItem('demo_user_id')
        this.getPlans()
        this.getRates()
        this.getCompany()
        this.getCompanySummary()
        this.getCountries()

        let tab = this.$route.query.tab
        if (tab) {
            this.active_tab = tab
        }
    },

    mounted() {
        this.fetchCampaigns(this.$route.params.id)
        this.setCompanyUsers(this.$route.params.id)
        this.setAllCompanyUsers(this.$route.params.id)
        this.getAdminAccountByCompanyId({company_id: this.$route.params.id})
        window.addEventListener('resize', this.updateLabelText)
        this.updateLabelText()
        this.initConversation()
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.onResize)
    },

    methods: {
        handleTabClick(tab, event) {
            // Don't update the filter query param when switching tabs
            const query = { ...this.$route.query }
            query.tab = tab.name

            // Only keep the filter param if we're staying on the compliance tab
            if (tab.name !== 'compliance') {
                delete query.filter
            }

            this.$router.push({ query }).catch(err => {
                // Ignore NavigationDuplicated errors
                if (err.name !== 'NavigationDuplicated') {
                    console.error(err)
                }
            })
        },

        getTrialPurgeDate(company) {
            return moment.utc(company.trial_ends_at).add(Plan.STANDARD_TRIAL_PERIOD, Plan.STANDARD_TRIAL_PERIOD_UNIT)
        },

        getHubspotCompanyLink(company) {
            let portal_id = 4592972
            if (['local', 'dev', 'mde', 'testing', 'staging'].includes(this.app_env)) {
                portal_id = 8446098
            }
            return `https://app.hubspot.com/contacts/${portal_id}/company/${company.hubspot_company_id}`
        },

        getHubspotDealLink(company) {
            let portal_id = 4592972
            if (['local', 'dev', 'mde', 'testing', 'staging'].includes(this.app_env)) {
                portal_id = 8446098
            }
            return `https://app.hubspot.com/contacts/${portal_id}/deal/${company.hubspot_deal_id}`
        },

        applySearch: _.debounce(function () {
            this.user_pagination.page = 1

            this.setCompanyUsers(this.$route.params.id)
        }, 300),

        paginateUsers(page) {
            this.user_pagination.page = page

            this.setCompanyUsers(this.$route.params.id)
        },

        changedUserTypeFilter() {
            this.user_pagination.page = 1

            this.setCompanyUsers(this.$route.params.id)
        },

        isAddressFieldDisabled(company) {
            return this.configurations_saving || company.registered_address || this.registering_address
        },

        searchAccounts(query) {
            this.assign_account.filters.search = query
            this.assign_account.loading = true
            const paginate = {
                page: this.assign_account.search_current_page,
                per_page: this.assign_account.search_per_page
            }
            axios.get('/api/admin/accounts', {
                params: Object.assign(paginate, this.assign_account.filters)
            }).then(res => {
                this.assign_account.current_page = res.data.current_page
                this.assign_account.accounts = res.data.data
                this.assign_account.loading = false
            }).catch(err => {
                console.log(err)
                this.assign_account.loading = false
                this.$root.handleErrors(err.response)
            })
        },

        updateAssignedAccounts() {
            this.assign_account.loading = true
            axios.put('/api/admin/user/' + this.assign_account.user_id + '/accesses', {
                'companies': this.assign_account.assigned
            }).then(res => {
                this.$notify({
                    offset: 95,
                    title: 'User',
                    message: res.data.message,
                    type: 'success'
                })
                this.setCompanyUsers(this.$route.params.id)
                this.assign_account.loading = false
                this.assign_account.show_dialog = false
            }).catch(err => {
                console.log(err)
                this.assign_account.loading = false
                this.assign_account.show_dialog = false
                this.$root.handleErrors(err.response)
            })
        },

        fetchCampaigns(company_id) {
            axios.get(`/api/admin/company/${company_id}/campaigns`)
                .then(res => {
                    this.campaigns = res.data
                })
                .catch(err => {
                    this.$notify.error({
                        title: 'Error loading company campaigns',
                        message: err.response.data.message,
                    })
                })
        },

        updateAssignedRoles() {
            // Do not update roles if it's still loading
            if (this.assign_roles.loading) {
                return
            }

            this.assign_roles.loading = true
            axios.put('/api/admin/user/' + this.assign_roles.user_id + '/roles', {
                'role': this.assign_roles.assigned
            }).then(res => {
                this.$notify({
                    offset: 95,
                    title: 'User',
                    message: res.data.message,
                    type: 'success'
                })
                this.setUserRoles(res.data.roles)
                this.assign_roles.loading = false
                this.assign_roles.show_dialog = false
            }).catch(err => {
                this.assign_roles.loading = false
                this.assign_roles.show_dialog = false
                this.$root.handleErrors(err.response)
            })
        },

        showAssignAccountDialog(user) {
            this.assign_account.user_id = user.id
            this.assign_account.accounts = []
            this.assign_account.assigned = []

            if (user && user.user_accesses && user.user_accesses.length > 0) {
                let current_company = this.company
                let accounts = []

                user.user_accesses.forEach(user => {
                    // Do not include current selected company / main company
                    if (current_company.id !== user.company_id && user.access_user_id !== user.company_id) {
                        let company = {
                            id: user.company_id,
                            name: user.company.name,
                        }

                        accounts.push(company)
                    }
                })

                this.assign_account.accounts = accounts
                this.assign_account.assigned = user.user_accesses.filter(access => {
                    // Do not include current selected company / main company
                    return current_company.id !== access.company_id && access.access_user_id !== access.company_id
                }).map(a => a.company_id)
            }

            this.assign_account.show_dialog = true
        },

        showRolesDialog(user) {
            this.assign_roles.assigned = []

            let current_roles = user.roles.map(role => role.name)

            this.customRoles.every(role => {
                // computed diff between required roles for custom and current roles
                let diff = role.roles.filter(r => !current_roles.includes(r))

                // if diff is empty means that user has the custom role
                if (diff.length === 0) {
                    this.assign_roles.assigned = role.id
                    return false
                }
                return true
            })

            this.assign_roles.user_id = user.id
            this.assign_roles.show_dialog = true
        },

        updatePlanSummary(planId) {
            if (planId) {
                this.selected_plan = this.plans.find(p => planId == p.id)
            }
        },

        login(user_id) {
            return this.impersonate(user_id)
        },

        getCurrentYearPassAndActiveMonths() {
            return moment.months().filter((month, index) => {
                if (index <= this.date.getMonth()) {
                    return month
                }
            })
        },

        getUserImpersonateDisabledReason(user) {
            if (!user.enabled) {
                return 'Cannot impersonate a suspended user.'
            }
            if (user.deleted_at != null) {
                return 'Cannot impersonate a deleted user.'
            }
            return null
        },

        impersonate(user_id) {
            this.promptImpersonation().then(reason => {
                let default_page = '/'
                let win = window.open('', '_blank')
                window.oldOpen = window.open
                window.open = function (url) {
                    win.location = url
                    window.open = oldOpen
                    win.focus()
                }

                this.auth.impersonate(user_id, false, reason).then(res => {
                    if (res.data?.app_url) {
                        default_page = res.data.app_url
                    }

                    window.open(default_page)
                }).catch(err => {
                    this.$notify.error({
                        title: 'Error impersonating user',
                        message: err?.response?.data?.error ?? Object.values(err?.response?.data?.errors ?? {}).flat()[0] ?? 'Error impersonating user',
                    })

                    window.open = oldOpen
                    win.close()
                })
            })
        },

        resetPassword(user) {
            this.user_form = _user_form()
            this.user_form.id = user.id
            this.user_form.first_name = user.first_name
            this.user_form.last_name = user.last_name
            this.user_form.phone_number = user.phone_number
            this.user_form.email = user.email

            this.$confirm(`Are you sure you want to reset the password for this user <strong>${user.name}</strong>. Continue?`, 'Warning', {
                confirmButtonText: 'OK',
                cancelButtonText: 'Cancel',
                customClass: 'width-500 fixed',
                dangerouslyUseHTMLString: true,
                type: 'warning'
            }).then(() => {
                this.loading = true
                axios.put('/api/admin/user/' + user.id + '/reset-password').then(res => {
                    this.user_form.password = res.data.password
                    this.$notify({
                        title: 'Password Reset',
                        message: res.data.data,
                        type: 'success',
                        dangerouslyUseHTMLString: true,
                        showClose: true,
                    })
                    this.loading = false
                }).catch(err => {
                    let error = err?.response?.data?.error
                    this.$notify({
                        title: 'Error',
                        message: error ? error : 'Something went wrong :(',
                        type: 'error',
                        showClose: true,
                    })
                    this.user_form = _user_form()
                    this.loading = false
                })
            }).catch(() => {
            })
        },

        restoreUser(user) {
            this.$confirm(`This will permanently restore the user <strong>${user.name}</strong> . Continue?`, 'Warning', {
                confirmButtonText: 'OK',
                cancelButtonText: 'Cancel',
                customClass: 'width-500 fixed',
                dangerouslyUseHTMLString: true,
                type: 'warning'
            }).then(() => {
                axios.put('/api/admin/user/' + user.id + '/restore').then(res => {
                    this.setCompanyUsers(this.$route.params.id)
                    this.$notify({
                        title: 'Restore',
                        message: `<strong>${res.data.name}</strong> restore completed`,
                        type: 'success',
                        dangerouslyUseHTMLString: true,
                        showClose: true,
                    })
                }).catch(err => {
                    let error_message = 'Something went wrong :('
                    if (typeof err.response.data.errors != 'undefined'
                        && typeof err.response.data.errors.error_message != 'undefined') {
                        error_message = err.response.data.errors.error_message
                    }
                    this.$notify({
                        title: 'Error',
                        message: error_message,
                        type: 'error',
                        dangerouslyUseHTMLString: true,
                        showClose: true,
                    })
                })
            }).catch(() => {
            })
        },

        deleteUserRemote(user_id) {
            this.userIdToDelete = user_id
            this.showDeletionModal = true
        },

        handleUserDeleted() {
            this.setCompanyUsers(this.$route.params.id)
        },

        getPlans() {
            this.loading_plans = true
            axios.get('/api/admin/plan', {
                params: {
                    all: true
                }
            }).then(res => {
                this.plans = res.data
                this.loading_plans = false
            }).catch(err => {
                this.loading_plans = false
                this.$root.handleErrors(err.response)
            })
        },

        getRates() {
            this.loading_rates = true
            axios.get('/api/admin/rate').then(res => {
                this.rates = res.data
                this.loading_rates = false
            }).catch(err => {
                this.loading_rates = false
                this.$root.handleErrors(err.response)
            })
        },

        cellEnter: _.throttle(function (row, column, cell, event) {
            this.visible_row_id = row.id
        }, 50),

        getCompanySummary() {
            this.loading_company = true
            return this.fetchCompanySummary(this.$route.params.id).then((res) => {
                if (typeof res.company.yesterday_usage !== 'undefined' &&
                    res.company.yesterday_usage !== null) {
                    res.company.yesterday_usage.twilio_usage = JSON.parse(res.company.yesterday_usage.twilio_usage)
                } else {
                    res.company.yesterday_usage = {}
                    res.company.yesterday_usage.twilio_usage = this.yesterday_balance_empty
                }
                const billing_admins = _.get(res.company, 'billing_admins', [])
                res.company.billing_admins = billing_admins.length ? billing_admins[0] : billing_admins
                res.company.diagnosis = res.diagnosis

                this.configurations[0] = false
                this.company_summary = [res.company]

                this.loading_company = false

                this.discount_data = res.discount_data

                this.show_chargebee_info = !!(res.company.subscription && res.company.subscription.chargebee_id)
            }).catch((err) => {
                this.$root.handleErrors(err.response)
                this.loading_company = false
            })
        },

        setCompanyUsers(company_id) {
            this.loading_company_users = true
            this.fetchUserByCompanyId(company_id, false)
                .then((result) => {
                    const active_users = []
                    const deleted_users = []
                    if (Array.isArray(result.data)) {
                        result.data.forEach((user) => {
                            if (!user.deleted_at) {
                                active_users.push(user)
                            } else {
                                deleted_users.push(user)
                            }
                        })
                        this.company_users = [...active_users, ...deleted_users]
                    }

                    this.user_pagination.total = result.total || 0
                    this.loading_company_users = false
                }).catch((err) => {
                this.loading_company_users = false
                this.$root.handleErrors(err.response)
            })
        },

        setAllCompanyUsers(company_id) {
            const params = { company_id, include_trashed: false, user_type_filter: 'Users' }

            return axios.get('/api/admin/user', { params })
                .then(res => {
                    if (Array.isArray(res.data)) {
                        this.all_company_users = res.data
                    }
                })
                .catch(err => this.$root.handleErrors(err.response))
        },

        fetchUserByCompanyId(company_id, hide_sub_users = true) {
            const parameters = {company_id: company_id}

            if (hide_sub_users) {
                parameters['hide_sub_users'] = hide_sub_users
            }

            parameters.user_type_filter = this.user_type_filter
            parameters.search = this.search
            parameters.page = this.user_pagination.page
            parameters.size = this.user_pagination.size
            parameters.paginate = true

            return axios.get('/api/admin/user', {params: parameters})
                .then(res => {
                    return Promise.resolve(res.data)
                }).catch(err => {
                    return Promise.reject(err)
                })
        },

        setUserRoles(roles) {
            // update the user in list
            let index = this.company_users.findIndex(user => user.id === this.assign_roles.user_id)
            this.company_users[index].roles = roles
        },

        fetchCompanySummary(company_id) {
            return axios.get('/api/admin/company/' + company_id + '/summary')
                .then(res => {
                    return Promise.resolve(res.data)
                }).catch(err => {
                    return Promise.reject(err)
                })
        },

        // this will load company admin account
        getAdminAccountByCompanyId(params) {
            this.loading_company_account = true
            axios.get('/api/admin/accounts', {
                params: params
            }).then(res => {
                this.loading_company_account = false
                if (res.data.length) {
                    this.company_account = res.data[0]
                }
            }).catch(err => {
                console.log(err)
                this.loading_company_account = false
                this.$notify({
                    title: 'Error',
                    message: 'Something went wrong :(',
                    type: 'error',
                    showClose: true,
                })
            })
        },

        tableLeave() {
            this.visible_row_id = null
        },

        getStyle(id) {
            let opacity = 0.5
            if (id == this.visible_row_id) {
                opacity = 1
            }
            return {
                opacity: opacity
            }
        },

        getCompanyCreatedSource(company) {
            const presignup_source = Object.values(PRE_SIGNUP_SUBMISSION_TYPES).find(submission_type => submission_type.id === company?.submission_type)
            return presignup_source?.visible_name || PRE_SIGNUP_SUBMISSION_TYPES.admin.visible_name
        },

        verifyUser(user) {
            // sanity check
            if (!user) {
                return
            }

            axios.post('/api/admin/user/' + user.id + '/verify-user')
                .then(res => {
                    this.$notify({
                        offset: 75,
                        title: 'User',
                        message: 'We have successfully verified the user.',
                        type: 'success'
                    })
                    let new_user = _.extend({}, user, res.data)
                    this.$set(this.company_users, this.company_users.indexOf(user), new_user)
                })
                .catch(err => {
                    this.$root.handleErrors(err.response)
                })
        },

        resendWelcomeEmail(user_id) {
            axios.post('/api/admin/user/' + user_id + '/resend-welcome-email')
                .then(res => {
                    this.$notify({
                        offset: 95,
                        title: 'User',
                        message: 'We have successfully sent the welcome email.',
                        type: 'success'
                    })
                })
                .catch(err => {
                    this.$root.handleErrors(err.response)
                })
        },

        suspendUser(user) {
            this.$confirm('Are you sure you want to suspend the user?', 'Suspend User', {
                confirmButtonText: 'Yes, I\'m sure',
                cancelButtonText: 'No, I\'m not',
                customClass: 'width-500 fixed',
                type: 'info'
            }).then(() => {
                axios.post('/api/admin/user/' + user.id + '/suspend')
                    .then(res => {
                        this.$notify({
                            offset: 75,
                            title: 'User',
                            message: 'We have successfully suspended the user.',
                            type: 'success'
                        })
                        this.updateUser(res.data)
                    }).catch(err => {
                    this.$root.handleErrors(err.response)
                })
            }).catch(err => {
                console.log(err)
            })
        },

        unsuspendUser(user) {
            this.$confirm('Are you sure you want to unsuspend the user?', 'Unsuspend User', {
                confirmButtonText: 'Yes, I\'m sure',
                cancelButtonText: 'No, I\'m not',
                customClass: 'width-500 fixed',
                type: 'info'
            }).then(() => {
                axios.post('/api/admin/user/' + user.id + '/unsuspend')
                    .then(res => {
                        this.$notify({
                            offset: 75,
                            title: 'User',
                            message: 'We have successfully unsuspended the user.',
                            type: 'success'
                        })
                        this.updateUser(res.data)
                    }).catch(err => {
                    this.$root.handleErrors(err.response)
                })
            }).catch(err => {
                console.log(err)
            })
        },

        initiatePurge(company) {
            this.loading_initiate_purge = true
            axios.post('/api/admin/company/' + company.id + '/initiate-purge')
                .then(res => {
                    this.$notify({
                        offset: 75,
                        title: 'Account',
                        message: res.data.message,
                        type: 'success'
                    })
                    this.updateCompanyProperties(res.data.company)
                    this.loading_initiate_purge = false
                }).catch(err => {
                this.$root.handleErrors(err.response)
                this.loading_initiate_purge = false
            })
        },

        cancelInitiatePurge(company) {
            this.loading_cancel_initiate_purge = true
            axios.post('/api/admin/company/' + company.id + '/cancel-initiate-purge')
                .then(res => {
                    this.$notify({
                        offset: 75,
                        title: 'Account',
                        message: res.data.message,
                        type: 'success'
                    })
                    this.updateCompanyProperties(res.data.company)
                    this.loading_cancel_initiate_purge = false
                }).catch(err => {
                this.$root.handleErrors(err.response)
                this.loading_cancel_initiate_purge = false
            })
        },

        suspendCompany(company) {
            this.$prompt('Why you are suspending this account?', 'Suspend Account', {
                confirmButtonText: 'Suspend',
                cancelButtonText: 'Cancel'
            }).then(({value}) => {
                if (!value) {
                    this.$notify.error({
                        title: 'Error',
                        message: 'You can not suspend an account without a reason.'
                    })
                    return
                }

                axios.post('/api/admin/company/' + company.id + '/suspend', {
                    reason: value
                }).then(res => {
                    this.$notify({
                        offset: 75,
                        title: 'Account',
                        message: res.data.message,
                        type: 'success'
                    })
                    this.updateCompanyProperties(res.data.company)
                }).catch(err => {
                    this.$notify({
                        title: 'Error',
                        message: err.response.data.message,
                        type: 'error',
                        showClose: true,
                    })
                })
            }).catch(err => {
                console.log(err)
            })
        },

        unsuspendCompany(company) {
            this.$confirm('Are you sure you want to unsuspend the account?', 'Unsuspend Account', {
                confirmButtonText: 'Yes, I\'m sure',
                cancelButtonText: 'No, I\'m not',
                customClass: 'width-500 fixed',
                type: 'info'
            }).then(res => {
                axios.post('/api/admin/company/' + company.id + '/unsuspend').then(res => {
                    this.$notify({
                        offset: 75,
                        title: 'Account',
                        message: 'We have successfully unsuspended the account.',
                        type: 'success'
                    })
                    this.updateCompanyProperties(res.data)
                }).catch(err => {
                    this.$notify({
                        title: 'Error',
                        message: err.response.data.message,
                        type: 'error',
                        showClose: true,
                    })
                })
            }).catch(err => {
                console.log(err)
            })
        },

        updateCompanyProperties(company) {
            for (let property in company) {
                this.company_summary[0][property] = company[property]
                this.company[property] = company[property]
            }

            setCookie(`should_update_company_data-${company.id}`, true, 1)
        },

        updateCompanyRate(rate) {
            this.company_summary[0].rate = rate
            this.company.rate = rate
        },

        addLog(company) {
            this.loading_add_log = true
            axios.post('/api/admin/company/' + company.id + '/log', this.add_log)
                .then(res => {
                    this.add_log.body = null
                    this.$refs['addLog-' + company.id].doClose()
                    this.$notify({
                        offset: 75,
                        title: 'Account',
                        message: 'Added Account log.',
                        type: 'success'
                    })
                    this.updateCompanyProperties(res.data)
                    this.loading_add_log = false
                })
                .catch(err => {
                    console.log(err)
                    this.$root.handleErrors(err.response)
                    this.loading_add_log = false
                })
        },

        updateUser(user) {
            let user_index = this.company_users.map(function (user) {
                return user.id
            }).indexOf(user.id)
            for (let property in user) {
                this.company_users[user_index][property] = user[property]
            }
        },

        changeRate(company) {
            const rate = this.rates.find((rate) => rate.id == this.change_rate.rate_id)
            if (!rate) {
                return
            }
            let change_rate = _.clone(this.change_rate)
            this.$confirm(`Are you sure you want to change rate from ${company.rate.name} to ${rate.name}?`, 'Change Rate', {
                confirmButtonText: 'Yes, I\'m sure',
                cancelButtonText: 'No, I\'m not',
                customClass: 'width-500 fixed',
                type: 'warning'
            }).then(() => {
                this.loading_change_rate = true
                axios.post('/api/admin/company/' + company.id + '/change-rate', change_rate).then((res) => {
                    this.loading_change_rate = false
                    this.$notify({
                        offset: 75,
                        title: 'Account',
                        message: 'Successfully changed the rate.',
                        type: 'success'
                    })
                    this.updateCompanyRate(rate)
                }).catch(err => {
                    this.loading_change_rate = false
                    this.$root.handleErrors(err.response)
                })
            }).catch(() => {
            })
        },

        changeMps(company) {
            const mph = this.change_max_mps.max_mps * 60 * 60
            this.$confirm(`Are you sure you want to change Max MPS to ${mph} per hour?`, 'Change Max MPS', {
                confirmButtonText: 'Yes, I\'m sure',
                cancelButtonText: 'No, I\'m not',
                customClass: 'width-500 fixed',
                type: 'warning'
            }).then(() => {
                axios.post('/api/admin/company/' + company.id + '/change-max-mps', this.change_max_mps).then((res) => {
                    this.change_max_mps.campaign_id = null
                    this.change_max_mps.max_mps = null
                    this.$notify({
                        offset: 75,
                        title: 'Account',
                        message: 'Successfully changed the Max MPS.',
                        type: 'success'
                    })
                }).catch(err => {
                    this.$root.handleErrors(err.response)
                })
            }).catch(() => {
            })
        },

        changePlan(company) {
            // find custom modgenius plan in selector component
            const plan = this.$refs.modgeniusSelector.plans.find(plan => plan.id == this.change_plan.plan_id)
            if (!plan) {
                return
            }

            // harcoding checking if is a downgrade
            if (+company.plan.id === 124 && +plan.id === 122) {
                this.$notify({
                    offset: 75,
                    title: 'Account',
                    message: 'You can\'t downgrade the plan.',
                    type: 'error'
                })

                return
            }

            const change_plan = _.clone(this.change_plan)
            this.$confirm(`Are you sure you want to change plan from ${company.plan.name} to ${plan.name}?`, 'Change Plan', {
                confirmButtonText: 'Yes, I\'m sure',
                cancelButtonText: 'No, I\'m not',
                customClass: 'width-500 fixed',
                type: 'warning'
            }).then(() => {
                this.loading_change_plan = true
                axios.post('/api/admin/company/' + company.id + '/change-plan', change_plan).then(res => {
                    this.loading_change_plan = false
                    this.$notify({
                        offset: 75,
                        title: 'Account',
                        message: 'Successfully changed the plan.',
                        type: 'success'
                    })
                }).catch(err => {
                    this.loading_change_plan = false
                    this.$root.handleErrors(err.response)
                })
            }).catch(() => {
            })
        },

        handleLocalPresencePurchased(company) {
            this.company.local_presence_packages = cloneDeep(company.local_presence_packages)
            this.company_summary[0].local_presence_packages = cloneDeep(company.local_presence_packages)
        },

        showLogs(logs) {
            this.logs = logs
            this.showLogsVisible = true
        },

        async updateFeature(company, feature) {
            if (!company || !feature) {
                return
            }

            let field = feature
            let fields = [
                'lrn_lookup_enabled',
                'transcription_enabled',
                'aloai_text_agents_enabled',
                'rcs_enabled',
                'mcp_enabled',
                'aloai_voice_inbound_agents_enabled',
                'aloai_voice_outbound_agents_enabled',
                'use_new_engine',
                'aloai_missed_call_handler_feature_enabled',
                'sms_enabled',
                'undnc_enabled',
                'force_opt_out_phrase',
                'mobile_logs_enabled',
                'send_messages_via_hubspot_workflows_enabled',
                'team_inbox_line_management_enhancements',
                'automated_international_traffic_enabled'
            ]

            if (!fields.includes(feature)) {
                field += '_enabled'
            }

            // Set loading state for dependent features
            const dependentFeatures = ['team_inbox_line_management_enhancements']
            if (dependentFeatures.includes(feature)) {
                this.loading_dependent_feature = true
            }

            this.company[field] = this.company[field] ? 0 : 1

            let feature_data = {
                enabled: this.company[field],
                feature: feature
            }

            let feature_name = _.startCase(feature_data.feature.replace('sms', 'SMS').replace('rvm', 'RVM').replace('_', ' ')).replace('A 2 P', 'A2P').replace('Lrn', 'LRN')

            if (feature === 'aloai_missed_call_handler_feature_enabled') {
                feature_name = 'AI Call Rescue'
            }

            await this.apiUpdateFeature(company.id, feature_data).then((res) => {
                this.$notify({
                    offset: 75,
                    title: 'Account',
                    message: 'Successfully updated the ' + feature_name + ' feature.',
                    type: 'success'
                })
                this.updateCompanyProperties(res.data)
            }).catch(err => {
                this.$root.handleErrors(err.response)
                this.company[field] = this.company[field] ? 0 : 1
                this.company_summary[0][field] = this.company[field]
            }).finally(() => {
                // Clear loading state for dependent features
                this.loading_dependent_feature = false
            })
        },

        apiUpdateFeature(company_id, feature_data) {
            return new Promise((resolve, reject) => {
                axios.post('/api/admin/company/' + company_id + '/update-features', feature_data).then((res) => {
                    resolve(res)
                }).catch(err => {
                    reject(err.response)
                })
            })
        },

        resetChangeRate() {
            this.change_rate.rate_id = null
            this.resetForm('change_rate_form')
        },

        resetChangePlan() {
            this.change_plan.plan_id = null
            this.resetForm('change_plan_form')
        },

        onShowConfiguration(company) {
            this.validated = false

            this.configurations_edit.hubspot_company_id = company.hubspot_company_id
            this.configurations_edit.hubspot_deal_id = company.hubspot_deal_id
        },

        beforeCloseConfiguration() {
            this.validated = false
            this.preValidateForm('edit_configurations', true)
        },

        updateConfigurations(company_id, index) {
            let params = {
                hubspot_company_id: this.configurations_edit.hubspot_company_id,
                hubspot_deal_id: this.configurations_edit.hubspot_deal_id
            }
            this.configurations_saving = true

            axios.post('/api/admin/company/' + company_id + '/update-configurations', params)
                .then(res => {
                    this.updateCompanyProperties(res.data)
                    this.$notify({
                        offset: 75,
                        title: 'Account',
                        message: 'Successfully updated the configurations.',
                        type: 'success'
                    })
                    this.configurations_saving = false
                    this.hideConfigurationDialog(index)

                    this.registering_address = true
                })
                .catch(err => {
                    this.$set(this.configurations, index, true)
                    this.$root.handleErrors(err.response)
                    this.configurations_saving = false
                })
        },

        preventSubmit() {
            return false
        },

        showConfigurationDialog(index) {
            this.$set(this.configurations, index, true)
        },

        hideConfigurationDialog(index) {
            this.$set(this.configurations, index, false)
        },

        getCompany() {
            this.company = {}
            this.loading = true
            axios.get(`/api/admin/company/${this.$route.params.id}`).then(res => {
                this.company = res.data
            }).catch(err => {
                this.$root.handleErrors(err.response)
                this.$router.push({name: 'Dashboard'})
            }).then(_ => {
                this.loading = false
            })
        },

        getChargebeeSubscriptionLink(company) {
            let site = localStorage.getItem('chargebee_site')
            if (!site) {
                if (this.app_env === 'staging') {
                    site = 'aloware-staging-test'
                } else if (['local', 'dev', 'mde', 'testing'].includes(this.app_env)) {
                    site = 'aloware-test'
                } else {
                    site = 'aloware'
                }
            }
            if (company.subscription) {
                return `https://${site}.chargebee.com/admin-console/subscriptions/${company.subscription.chargebee_id}`
            }
            return false
        },

        showUserMinSeat(company) {
            this.invalid_minimum_license_count = false
            this.min_user_seat_validation_message = ''
            this.min_user_seat = company.min_user_seat

            this.$nextTick(() => {
                this.$refs.min_user_seat.focus()
            })
        },

        hideUserMinSeatDialog() {
            this.dialog_minimum_license_count_visible = false
        },

        validateMinimumLicenseCount(value) {
            if (value === undefined || value <= 0) {
                this.invalid_minimum_license_count = true
                this.min_user_seat_validation_message = 'Value must be greater than 0.'
                return false
            }

            // Check if current company has a startup package plan
            if (this.company && this.company.plan && STARTUP_PACKAGES.includes(this.company.plan.name) && value > 1) {
                this.invalid_minimum_license_count = true
                this.min_user_seat_validation_message = 'Maximum allowed value for the Startup package is 1.'
                return false
            }

            this.invalid_minimum_license_count = false
            this.min_user_seat_validation_message = ''
            return true
        },

        updateMinimumLicenseCount(value) {
            if (!this.validateMinimumLicenseCount(value)) {
                return false
            }

            this.invalid_minimum_license_count = false
            this.min_user_seat_validation_message = ''

            const message = 'Are you sure you want to change the Minimum License Count?'

            this.$confirm(message, 'Change Minimum License Count', {
                confirmButtonText: 'Yes, I\'m sure',
                cancelButtonText: 'No, I\'m not',
                customClass: 'width-500 fixed',
                type: 'warning'
            }).then(() => {
                axios.post(`/api/admin/company/${this.company.id}/update-min-user-seat`, {
                    min_user_seat: value
                }).then(res => {
                    this.updateCompanyProperties(res.data)

                    this.$notify({
                        offset: 75,
                        title: 'Minimum License Count',
                        message: 'Successfully updated Minimum License Count',
                        type: 'success'
                    })

                    // queue in thread so it wouldn't overlap
                    setTimeout(() => {
                        this.$notify({
                            offset: 75,
                            title: 'Account',
                            message: 'Added Account log.',
                            type: 'success',
                        })
                    }, 0)
                }).catch(err => {
                    this.$root.handleErrors(err.response)

                    // keep it open for change
                    this.dialog_minimum_license_count_visible = true
                })
            }).catch(err => {
                // keep it open for change
                this.dialog_minimum_license_count_visible = true
            })
        },

        showMaxNegativeBalance(company) {
            this.invalid_max_negative_balance = false
            this.configurations_edit.max_negative_balance = company.max_negative_balance

            this.$nextTick(() => {
                this.$refs.max_negative_balance.focus()
            })
        },

        hideMaxNegativeBalanceDialog() {
            this.dialog_max_negative_balance_visible = false
        },

        validateMaxNegativeBalance(value) {
            // ensure valid input value
            if (value === undefined || value > 0) {
                this.invalid_max_negative_balance = true
                return false
            }

            return true
        },

        updateMaxNegativeBalance(value) {
            if (!this.validateMaxNegativeBalance(value)) {
                return false
            }

            this.invalid_max_negative_balance = false

            const message = 'Are you sure you want to change the max negative balance?'

            this.$confirm(message, 'Change Max Negative Balance', {
                confirmButtonText: 'Yes, I\'m sure',
                cancelButtonText: 'No, I\'m not',
                customClass: 'width-500 fixed',
                type: 'warning'
            }).then(() => {
                axios.post(`/api/admin/company/${this.company.id}/update-max-negative-balance`, {
                    max_negative_balance: value
                }).then(res => {
                    this.updateCompanyProperties(res.data)

                    this.$notify({
                        offset: 75,
                        title: 'Max Negative Balance',
                        message: 'Successfully updated the Max Negative Balance',
                        type: 'success'
                    })

                    // queue in thread so it wouldn't overlap
                    setTimeout(() => {
                        this.$notify({
                            offset: 75,
                            title: 'Account',
                            message: 'Added Account log.',
                            type: 'success',
                        })
                    }, 0)
                }).catch(err => {
                    this.$root.handleErrors(err.response)

                    // keep it open for change
                    this.dialog_max_negative_balance_visible = true
                })
            }).catch(err => {
                // keep it open for change
                this.dialog_max_negative_balance_visible = true
            })
        },

        getCountries() {
            const countries = window.countriesAndTimezones.getAllCountries()
            const countriesArr = Object.keys(countries).map((key) => {
                return countries[key]
            })

            let mappedCountries = countriesArr.map((country) => {
                if (!['US', 'CA'].includes(country.id)) {
                    return {
                        id: country.id,
                        name: country.name
                    }
                }
            }).filter((country) => country !== undefined)
            mappedCountries.unshift({
                id: 'CA',
                name: 'Canada'
            })

            mappedCountries.unshift({
                id: 'US',
                name: 'United States'
            })

            this.countries = mappedCountries
        },

        toggleSmsCapability(val) {
            if (val) {
                this.enableSmsCapability()
            } else {
                this.disableSmsCapability()
            }
        },

        enableSmsCapability() {
            this.$confirm('Are you sure?', 'SMS Capability', {
                confirmButtonText: 'Yes, I\'m sure',
                cancelButtonText: 'No, I\'m not',
                customClass: 'width-500 fixed',
                type: 'info'
            }).then((val) => {
                axios.post('/api/admin/company/' + this.company.id + '/capabilities/sms')
                    .then(res => {
                        this.$notify({
                            offset: 75,
                            title: 'Company',
                            message: "Company's SMS capability is now enabled.",
                            type: 'success'
                        })
                    })
                    .catch(err => {
                        this.$root.handleErrors(err.response)
                    })
            }).catch(err => {
                this.company_summary[0].sms_enabled = false
            })
        },

        disableSmsCapability() {
            this.$confirm('Are you sure? Disabling this will affect features such as broadcasts and sequences.', 'SMS Capability', {
                confirmButtonText: 'Yes, I\'m sure',
                cancelButtonText: 'No, I\'m not',
                customClass: 'width-500 fixed',
                type: 'info'
            }).then(() => {
                axios.delete('/api/admin/company/' + this.company.id + '/capabilities/sms')
                    .then(res => {
                        this.$notify({
                            offset: 75,
                            title: 'Company',
                            message: "Company's SMS capability is now disabled.",
                            type: 'success'
                        })
                    })
                    .catch(err => {
                        this.$root.handleErrors(err.response)
                    })
            }).catch(err => {
                this.company_summary[0].sms_enabled = true
            })
        },

        changeAutoRecharge() {
            this.loading_auto_recharge = true
            let params = {
                auto_recharge: this.auto_recharge,
            }
            if (this.auto_recharge) {
                params.recharge_for_amount = this.recharge_for_amount
                params.auto_recharge_threshold = this.auto_recharge_threshold
            }
            axios.post('/api/admin/company/' + this.company.id + '/update-auto-recharge', params).then(res => {
                this.loading_auto_recharge = false
                this.$notify({
                    offset: 95,
                    title: 'Billing',
                    message: 'Auto recharge request has been processed.',
                    type: 'success',
                    showClose: true
                })
                this.company = res.data
            }).catch(err => {
                this.loading_auto_recharge = false
                this.$root.handleErrors(err.response)
            })
        },

        checkAutoRecharge(status) {
            if (status == false) {
                this.changeAutoRecharge()
            }
        },

        onShowInternationalSupport(company) {
            this.international_tier = company.international_tier
            this.international_tier_request_note = ''
        },

        changeInternationalTier(company) {
            // Validate that note is provided when tier is 4
            if (this.isTier4NoteDisabled) {
                this.$notify({
                    offset: 75,
                    title: 'Validation Error',
                    message: 'A note is required when selecting Tier 4.',
                    type: 'warning'
                })

                return
            }

            this.loading_international_tier = true

            const payload = {
                tier: this.international_tier
            }

            // Include request_note when tier is 4
            if (this.isTier4Selected) {
                payload.request_note = this.international_tier_request_note.trim()
            }

            axios.put('/api/admin/company/' + company.id + '/change-international-tier', payload)
                .then((res) => {
                    this.loading_international_tier = false

                    // Determine success message based on response
                    let message = 'Successfully changed the international tier.'
                    if (this.isTier4Selected && !this.isInternationalAdmin && res.data.message) {
                        message = res.data.message
                    }

                    this.$notify({
                        offset: 75,
                        title: 'Account',
                        message: message,
                        type: 'success'
                    })

                    if (res.data.company) {
                        company.international_tier = res.data.company.international_tier
                    } else {
                        company.international_tier = res.data.international_tier
                    }

                    // Clear the note after successful submission
                    this.international_tier_request_note = ''
                })
                .catch(err => {
                    this.loading_international_tier = false
                    this.$root.handleErrors(err.response)
                })
        },

        updateLabelText() {
            const isNarrowScreen = window.innerWidth < 1700
            this.allow_messaging_from_hubspot_label = isNarrowScreen ? 'HubSpot Messaging' : 'Allow Messaging from HS Workflow'
            this.set_toll_free_as_calling_only_label = isNarrowScreen ? 'Toll-Free to Calling Only' : 'Set Toll-Free Lines as Calling Only'
            this.set_10dlc_as_calling_only_label = isNarrowScreen ? '10DLC to Calling Only' : 'Set 10DLC Lines as Calling Only'
            this.allow_outbound_sms_mms_label = isNarrowScreen ? 'Outbound SMS/MMS' : 'Allow Outbound SMS/MMS'
        },

        toggleUserMobileLogs(user) {
            // Store original state in case of error
            const originalState = user.mobile_logs_enabled

            // Optimistically update UI
            user.mobile_logs_enabled = !user.mobile_logs_enabled

            axios.post(`/api/admin/user/${user.id}/toggle-mobile-logs`)
                .then(res => {
                    // Update with actual state from server
                    user.mobile_logs_enabled = res.data.mobile_logs_enabled

                    this.$notify({
                        offset: 75,
                        title: 'Success',
                        message: res.data.message,
                        type: 'success'
                    })
                })
                .catch(err => {
                    // Revert to original state on error
                    user.mobile_logs_enabled = originalState

                    this.$root.handleErrors(err.response)
                })
        },

        openBraintree(company) {
            if (!company.subscription || !company.subscription.chargebee_id) {
                this.$notify({
                    title: 'Error',
                    message: 'No Chargebee subscription found for this account.',
                    type: 'error',
                    showClose: true,
                })
                return
            }

            this.braintreeLoading = true
            // Call the new API endpoint to get the Braintree customer URL
            axios.get(`/api/admin/company/${company.id}/braintree-customer-url`)
                .then(res => {
                    this.braintreeLoading = false
                    if (res.data && res.data.url) {
                        window.open(res.data.url, '_blank')
                    } else {
                        this.$notify({
                            title: 'Error',
                            message: 'No Braintree customer URL found.',
                            type: 'error',
                            showClose: true,
                        })
                    }
                })
                .catch(err => {
                    this.braintreeLoading = false
                    this.$notify({
                        title: 'Error',
                        message: err.response?.data?.message || 'Failed to fetch Braintree customer information.',
                        type: 'error',
                        showClose: true,
                    })
                })
        },

        overridePaymentRequirement(company) {
            this.paymentRequirementOverrideLoading = true
            axios.post(`/api/admin/company/${company.id}/override-payment-requirement`)
                .then(res => {
                    this.paymentRequirementOverrideLoading = false
                    company.is_card_added = true
                    this.$notify({
                        title: 'Success',
                        message: 'Payment requirement overridden.',
                        type: 'success'
                    })
                })
                .catch(err => {
                    this.paymentRequirementOverrideLoading = false
                    this.$root.handleErrors(err.response)
                })
        },

        scrollToBottom() {
            this.$nextTick(() => {
                const container = this.$refs.conversationContainer
                if (container) {
                    container.scrollTop = container.scrollHeight
                }
            })
        },

        scrollToTop() {
            this.$nextTick(() => {
                const container = this.$refs.conversationContainer
                if (container) {
                    // Get the last message index
                    const lastMessageIndex = this.conversation_history.length - 1
                    const lastMessageRef = this.$refs[`message-${lastMessageIndex}`]

                    if (lastMessageRef && lastMessageRef[0]) {
                        // Scroll to the top of the last message
                        lastMessageRef[0].scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                }
            })
        },

        handleEnter(e) {
            if (!e.ctrlKey && !e.metaKey) {
                e.preventDefault()
                this.askAloAi()
            }
        },

        newline(e) {
            // Allow the default behavior for Ctrl/Cmd + Enter
        },

        askAloAi() {
            if (!this.aloai_prompt.trim()) {
                this.$notify({
                    offset: 95,
                    title: 'Error',
                    message: 'Please enter a question',
                    type: 'error'
                })
                return
            }

            // Cancel any existing request
            if (this.cancelTokenSource) {
                this.cancelTokenSource.cancel()
            }
            this.cancelTokenSource = axios.CancelToken.source()

            this.aloai_loading = true
            const payload = {
                prompt: this.aloai_prompt,
                model: 'gpt-4o-mini',
                conversation_id: this.conversation_id
            }

            // Add user message to conversation immediately
            this.conversation_history.push({
                prompt: this.aloai_prompt,
                response: this.aloai_prompt,
                timestamp: new Date(),
                isUser: true
            })

            // Clear the input immediately
            this.aloai_prompt = ''

            // Scroll to top after adding user message
            this.scrollToTop()

            // Store the current request
            axios.post(`/api/admin/company/${this.company.id}/analyze`, payload, {
                cancelToken: this.cancelTokenSource.token
            }).then(response => {
                if (response.data.conversation_id) {
                    this.conversation_id = response.data.conversation_id
                }

                // Handle response format - use output_text from new format
                if (response.data.output_text) {
                    this.aloai_response = this.md.render(response.data.output_text)
                } else if (response.data.output) {
                    // Fallback to old format if needed
                    if (Array.isArray(response.data.output)) {
                        // Handle array output format
                        this.aloai_response = this.md.render(response.data.output[0].content[0].text)
                    } else if (response.data.output.content) {
                        // Handle object output format with content
                        this.aloai_response = this.md.render(response.data.output.content[0].text)
                    }
                }

                // Add AI response to conversation history
                this.conversation_history.push({
                    prompt: '',
                    response: this.aloai_response,
                    timestamp: new Date(),
                    isUser: false
                })

                // Scroll to top after adding AI response
                this.scrollToTop()
            }).catch(error => {
                // Only show error if it's not a cancelled request
                if (error.name !== 'CanceledError') {
                    this.$notify({
                        title: 'Error',
                        message: error.response?.data?.error || 'Failed to get response from AloAi',
                        type: 'error'
                    })
                }
            }).finally(() => {
                this.aloai_loading = false
                this.cancelTokenSource = null
            })
        },

        useRecommendation(prompt) {
            this.aloai_prompt = prompt
            // Focus the input after setting the prompt
            this.$nextTick(() => {
                const textarea = this.$el.querySelector('.el-textarea__inner')
                if (textarea) {
                    textarea.focus()
                }
            })
        },

        initConversation() {
            this.conversation_id = null
            this.conversation_history = []
        },

        clearChat() {
            // Cancel any ongoing request
            if (this.cancelTokenSource) {
                this.cancelTokenSource.cancel()
                this.cancelTokenSource = null
            }
            this.conversation_history = []
            this.conversation_id = null
        },

        canUnsuspendAccount(company) {
            return company.purging_status === Company.PURGING_STATUS_NOT_USED && this.canManageAccounts
        },

        handleTeamInboxLineManagementEnhancementsChange(company) {
            // If disabling, proceed normally
            if (!company.team_inbox_line_management_enhancements) {
                this.updateFeature(company, 'team_inbox_line_management_enhancements')
                return
            }

            // If enabling, show confirmation dialog
            this.$confirm(
                `
                  This is a BETA feature that will remove the line visibility and private line settings,
                  and will replace line visibility with Inbox Caller Id visibility.<br>
                  Do you confirm enabling it for this company?
                `,
                'Confirm BETA Feature',
                {
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No',
                    type: 'warning',
                    customClass: 'width-500 fixed',
                    dangerouslyUseHTMLString: true
                }
            ).then(() => {
                // User confirmed, proceed with enabling
                this.updateFeature(company, 'team_inbox_line_management_enhancements')
            }).catch(() => {
                // User cancelled, revert the switch
                company.team_inbox_line_management_enhancements = false
            })
        },

        showAloAiThrottleSettings(company) {
            this.aloai_throttle_settings.aloai_max_concurrent_outbound_calls = company.aloai_max_concurrent_outbound_calls || 2
            //this.aloai_throttle_settings.aloai_max_concurrent_inbound_calls = company.aloai_max_concurrent_inbound_calls || 5
        },

        updateAloAiThrottleSettings() {
             this.aloai_throttle_saving = true
             axios.post(`/api/admin/company/${this.company.id}/update-aloai-throttle-settings`, this.aloai_throttle_settings)
                 .then(res => {
                     this.aloai_throttle_saving = false
                     this.$notify({
                         offset: 75,
                         title: 'AloAi Throttle Settings',
                         message: 'Successfully updated AloAi throttle settings.',
                         type: 'success'
                     })
                     this.updateCompanyProperties(res.data)
                     this.hideAloAiThrottleDialog()
                 }).catch(err => {
                     this.aloai_throttle_saving = false
                     this.$root.handleErrors(err.response)
                 })
         },

        hideAloAiThrottleDialog() {
            this.aloai_throttle_dialog_visible = false
        },
    },

    watch: {
        $route({params, query}) {
            if (query.tab) {
                this.active_tab = query.tab
            }
        },

        company(value) {
            this.auto_recharge = value.auto_recharge
            this.recharge_for_amount = value.recharge_for_amount
            this.auto_recharge_threshold = value.auto_recharge_threshold
        }
    }
}
</script>

<style scoped>
.message-wrapper {
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
}

.message-wrapper.message-outbound {
    align-items: flex-end;
}

.message-content {
    max-width: 80%;
}

.message-bubble {
    background-color: #f8f9fa;
    border-radius: 15px;
    padding: 12px 16px;
    margin-bottom: 4px;
    position: relative;
}

.message-bubble-user {
    background-color: #e3f2fd;
}

.message-timestamp {
    font-size: 0.75rem;
    margin-top: 2px;
}

.message-outbound .message-timestamp {
    text-align: right;
}

.conversation-container {
    max-height: 500px;
    overflow-y: auto;
    padding-right: 0.5rem;
}

.conversation-container::-webkit-scrollbar {
    width: 6px;
}

.conversation-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.conversation-container::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
}

.conversation-container::-webkit-scrollbar-thumb:hover {
    background: #555;
}

.typing-indicator {
    display: flex;
    gap: 4px;
    padding: 4px 0;
}

.typing-indicator span {
    width: 8px;
    height: 8px;
    background-color: #909399;
    border-radius: 50%;
    display: inline-block;
    animation: typing 1s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
    animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes typing {
    0%, 100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-4px);
    }
}

.recommendations {
    border-radius: 8px;
}

.recommendations-label {
    font-size: 0.875rem;
    font-weight: 500;
}

.recommendations-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.recommendation-tag {
    cursor: pointer;
    transition: all 0.2s ease;
    font-weight: 600;
    font-size: 13px;
}

.recommendation-tag:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.chat-header {
    padding: 0.5rem 0;
    border-bottom: 1px solid #dee2e6;
}

.chat-header h5 {
    color: #495057;
    font-weight: 500;
}

.btn-outline-secondary {
    color: #6c757d;
    border-color: #dee2e6;
}

.btn-outline-secondary:hover {
    background-color: #f8f9fa;
    color: #dc3545;
    border-color: #dc3545;
}

.btn-outline-danger {
    color: #dc3545;
    border-color: #dc3545;
}

.btn-outline-danger:hover {
    background-color: #dc3545;
    color: #fff;
    border-color: #dc3545;
}
</style>
